import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Checklist, VehicleSide, DamageType, ChecklistDetailDto, ChecklistSectionDto, ChecklistItemDetailDto, CreateChecklistRequest, CreateChecklistItem } from '../../../models/Checklist';
import { ChecklistService } from '../../../services/checklist.service';
import { ChecklistPdfService } from '../../../services/checklist-pdf.service';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  CHECKLIST_ACCESSORY_GROUPS,
  CHECKLIST_ACCESSORY_TEMPLATE_IDS,
  CHECKLIST_PHOTO_SIDE_TO_TEMPLATE,
  CHECKLIST_TEMPLATE_IDS,
  accessoryFormKey,
  buildAccessoryFormControls,
  getAccessoryGroupTitleByTemplateId,
  isChecklistPdfAttachment,
  mapConditionToSna,
  mapSnaToCondition
} from '../../../config/checklist-template.config';

type Step = 1 | 2 | 3 | 4 | 5 | 6;

const T = CHECKLIST_TEMPLATE_IDS;

@Component({
  selector: 'app-checklist-form',
  templateUrl: './checklist-form.component.html',
  styleUrls: ['./checklist-form.component.css']
})
export class ChecklistFormComponent implements OnInit, OnDestroy {
  isEdit = false;
  isViewMode = false; // Modo de visualização (read-only)
  id?: number;
  contractIdFrom?: number; // ID do contrato de origem (se veio da visualização do contrato)
  step: Step = 1;
  totalSteps = 6;
  selectedChecklistType: string = 'Inspeção'; // Tipo de checklist selecionado (padrão: Inspeção)
  checklistDetail: ChecklistDetailDto | null = null; // Dados completos do checklist para visualização
  stepMeta = [
    { id: 1 as Step, title: 'Iniciar', subtitle: 'Veículo e condutor' },
    { id: 2 as Step, title: 'Condições', subtitle: 'KM, pneus e combustível' },
    { id: 3 as Step, title: 'Imagens', subtitle: 'Fotos por lado + detalhes' },
    { id: 4 as Step, title: 'Acessórios', subtitle: 'Checklist S/N/A' },
    { id: 5 as Step, title: 'Observações', subtitle: 'Anotações gerais' },
    { id: 6 as Step, title: 'Assinatura', subtitle: 'Confirmação' }
  ];

  vehicleForm!: FormGroup;  // Etapa 1
  conditionsForm!: FormGroup; // Etapa 2
  photosRequired: Record<VehicleSide, string | null> = {
    frente: null,
    direita: null,
    esquerda: null,
    cima: null,
    traseira: null
  };
  photoDetails: { side: VehicleSide; file?: File; url?: string; damageType?: DamageType; note?: string }[] = [];
  generatedPdfBase64: string | null = null; // PDF gerado após assinatura (para exibição)
  generatedPdfBlob: Blob | null = null; // PDF gerado como Blob (para upload)
  isGeneratingPdf = false; // Flag para controlar o estado de geração do PDF

  accessoriesForm!: FormGroup; // Etapa 4
  observationsForm!: FormGroup; // Etapa 5
  signatureForm!: FormGroup; // Etapa 6

  isLoading = false;
  isLoadingPlateDetection = false;
  isCancellingChecklist = false;
  sides: VehicleSide[] = ['frente','direita','esquerda','cima','traseira'];

  /** Grupos de acessórios do template Loca9Motos (seção Accessories). */
  accessoryGroups = CHECKLIST_ACCESSORY_GROUPS;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public router: Router, // Público para uso no template
    private service: ChecklistService,
    private pdfService: ChecklistPdfService,
    private contractsService: ContractsService,
    private ngZone: NgZone,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id')) || undefined;
    // Verificar se está em modo de visualização (rota sem /edit) ou edição
    const url = this.route.snapshot.url.map(segment => segment.path).join('/');
    this.isViewMode = !!this.id && !url.includes('edit');
    this.isEdit = !!this.id && !this.isViewMode;
    
    // Ler o tipo de checklist da query string (se fornecido)
    const typeParam = this.route.snapshot.queryParamMap.get('type');
    if (typeParam) {
      this.selectedChecklistType = typeParam;
    }
    
    // Ler o contractId de origem (se veio da visualização do contrato)
    const fromContractParam = this.route.snapshot.queryParamMap.get('fromContract');
    if (fromContractParam) {
      this.contractIdFrom = Number(fromContractParam);
    }

    const prefillPlateParam = this.route.snapshot.queryParamMap.get('plate');

    const now = new Date();

    // Para testes, sem campos obrigatórios
    this.vehicleForm = this.fb.group({
      plate: [''],
      model: [''],
      year: [null],
      odometerKm: [null],
      renavam: [''],
      chassis: [''],
      checklistDate: [null],
      checklistTime: [null],
      driverName: [''],
      driverCpf: [''],
      driverPhone: ['']
    });

    this.conditionsForm = this.fb.group({
      panelKm: [null],
      tireCondition: [null],
      fuelLevel: [null],
      isDirty: [null]
    });

    this.accessoriesForm = this.fb.group(buildAccessoryFormControls());

    this.observationsForm = this.fb.group({
      notes: ['']
    });

    this.signatureForm = this.fb.group({
      name: [''],
      cpf: [''],
      phone: [''],
      email: [''],
      signatureDataUrl: ['']
    });

    if (!this.id && !this.contractIdFrom && prefillPlateParam) {
      this.vehicleForm.patchValue({
        plate: prefillPlateParam.trim().toUpperCase()
      });
    }

    // Carregar dados se estiver em modo de edição ou visualização
    if (this.id) {
      if (this.contractIdFrom) {
        this.isLoading = true; // já mostrar loading ao abrir do contrato, até prefill terminar
      }
      this.loadData();
    } else if (this.contractIdFrom) {
      // Abriu pelo contrato: apenas abrir a rotina e preencher com dados do contrato (não chama endpoint de criar)
      this.isLoading = true;
      this.loadContractDataAndPrefill();
    }
  }

  /**
   * Quando o form é aberto em modo "new" com fromContract: apenas busca dados do contrato
   * e preenche o form (placa, chassi, RENAVAM, nome, telefone etc.). Não chama o endpoint
   * checklist/contract/:id/b2c/full — só abre a rotina do checklist já preenchida.
   */
  private loadContractDataAndPrefill(): void {
    if (!this.contractIdFrom) return;
    this.isLoading = true;
    this.contractsService.getContractComplete(this.contractIdFrom).subscribe({
      next: (contractData: any) => {
        this.applyContractDataToForms(contractData);
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.showError(
          'Erro ao carregar dados do contrato: ' + (err?.error?.message || err?.message || 'Erro desconhecido'),
          'error',
          true,
          6000
        );
        this.isLoading = false;
      }
    });
  }

  loadData(): void {
    if (!this.id) return;
    this.isLoading = true;
    this.service.getById(this.id, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const checklist: ChecklistDetailDto = response.data;
          this.checklistDetail = checklist; // Armazenar dados completos para visualização
          
          // Se estiver em modo de visualização, não precisa mapear para formulários
          if (!this.isViewMode) {
            this.mapChecklistDataToForms(checklist);
            // Quando veio do contrato (edit): preencher com dados do contrato (placa, chassi, RENAVAM, nome, telefone etc.)
            if (this.contractIdFrom) {
              this.prefillFromContract();
              return;
            }
          }
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  /**
   * Aplica dados do contrato nos formulários (veículo e condutor).
   * Usado quando os dados vêm do state da navegação ou da API getContractComplete.
   */
  private applyContractDataToForms(contractData: any): void {
    const v = contractData?.vehicleSnapshot || contractData?.vehicle || {};
    const d = contractData?.driver || {};
    const plate = v.plate ?? v.plateNumber ?? '';
    const chassis = v.chassis ?? v.chassisNumber ?? '';
    const renavam = v.renavam ?? '';
    const modelStr = [v.brand, v.model].filter(Boolean).join(' ').trim() || v.model || '';
    const year = v.year ?? v.modelYear ?? null;
    const km = v.currentKm ?? v.currentMileage ?? null;
    const driverName = d.name ?? '';
    const driverCpf = d.cpfCnpj ?? d.cpf ?? '';
    const driverPhone = d.phone ?? '';
    const driverEmail = d.email ?? '';
    this.vehicleForm.patchValue({
      plate,
      model: modelStr,
      year,
      odometerKm: km,
      renavam,
      chassis,
      driverName,
      driverCpf,
      driverPhone
    });
    this.signatureForm.patchValue({
      name: driverName,
      cpf: driverCpf,
      phone: driverPhone,
      email: driverEmail
    });
  }

  /**
   * Preenche vehicleForm e signatureForm com dados do contrato via API.
   * Regra: ao clicar em "criar checklist" no contrato, os dados já vêm preenchidos (placa, chassi, RENAVAM, nome, telefone etc.).
   */
  private prefillFromContract(): void {
    if (!this.contractIdFrom) return;
    this.contractsService.getContractComplete(this.contractIdFrom).subscribe({
      next: (contractData: any) => {
        this.applyContractDataToForms(contractData);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  /**
   * Mapeia os dados da nova estrutura da API para os formulários
   */
  private mapChecklistDataToForms(checklist: ChecklistDetailDto): void {
    // Extrair data e hora do ChecklistDate
    const checklistDate = checklist.ChecklistDate ? new Date(checklist.ChecklistDate) : null;
    const dateStr = checklistDate ? checklistDate.toISOString().split('T')[0] : null;
    const timeStr = checklistDate 
      ? `${checklistDate.getHours().toString().padStart(2, '0')}:${checklistDate.getMinutes().toString().padStart(2, '0')}`
      : '';

    // Mapear dados do veículo e condutor
    // Placa deve vir do item do checklist (template ID 22) ao invés de VehiclePlate
    const plateFromItem = this.findItemValueByTemplateId(checklist.Sections, T.plate) || checklist.VehiclePlate || '';
    const mileageFromItem =
      this.findItemNumberValueByTemplateId(checklist.Sections, T.odometer) || checklist.Mileage || null;
    
    this.vehicleForm.patchValue({
      plate: plateFromItem,
      model: this.findItemValue(checklist.Sections, 'Modelo do Veículo') || '',
      year: null, // Não disponível na nova estrutura
      odometerKm: mileageFromItem,
      renavam: this.findItemValue(checklist.Sections, 'RENAVAM') || '',
      chassis: this.findItemValue(checklist.Sections, 'Chassi') || '',
      checklistDate: dateStr,
      checklistTime: timeStr,
      driverName: checklist.DriverName || '',
      driverCpf: checklist.DriverCPF || '',
      driverPhone: checklist.DriverPhone || ''
    });

    // Mapear condições
    this.conditionsForm.patchValue({
      panelKm: checklist.DashboardMileage || null,
      tireCondition: this.mapTireCondition(checklist.TireCondition),
      fuelLevel: this.mapFuelLevel(checklist.FuelLevel),
      isDirty: checklist.IsVehicleDirty || false
    });

    // Mapear acessórios das Sections
    const accessoriesData = this.extractAccessoriesFromSections(checklist.Sections);
    this.accessoriesForm.patchValue(accessoriesData);

    // Mapear observações
    const observations = checklist.Observations || this.findItemValue(checklist.Sections, 'Observações Gerais') || '';
    this.observationsForm.patchValue({ notes: observations });

    // Mapear assinatura
      this.signatureForm.patchValue({
      name: checklist.DriverName || '',
      cpf: checklist.DriverCPF || '',
      phone: checklist.DriverPhone || '',
      email: checklist.DriverEmail || '',
      signatureDataUrl: checklist.DriverSignature || ''
    });

    // Mapear fotos das Sections
    this.mapPhotosFromSections(checklist.Sections);
  }

  /**
   * Extrai valores de acessórios das Sections
   */
  private extractAccessoriesFromSections(sections: ChecklistSectionDto[]): Record<string, string> {
    const accessories: Record<string, string> = {};
    const accessoriesSection = sections.find((s) => s.SectionType === 'Accessories');

    if (accessoriesSection?.Items) {
      accessoriesSection.Items.forEach((item) => {
        const templateId = item.ChecklistTemplateItemId;
        if (templateId == null || !CHECKLIST_ACCESSORY_TEMPLATE_IDS.includes(templateId)) {
          return;
        }
        const key = accessoryFormKey(templateId);
        const raw = item.Condition || item.Value || item.TextValue || null;
        const sna = mapConditionToSna(raw as string);
        if (sna) {
          accessories[key] = sna;
        }
      });
    }

    return accessories;
  }

  /** Agrupa itens de acessórios por categoria (Description) na visualização. */
  getAccessoryViewGroups(items: ChecklistItemDetailDto[]): { title: string; items: ChecklistItemDetailDto[] }[] {
    const valid = this.getValidItems(items);
    const map = new Map<string, ChecklistItemDetailDto[]>();
    for (const item of valid) {
      const templateId = item.ChecklistTemplateItemId;
      const title =
        templateId != null
          ? getAccessoryGroupTitleByTemplateId(templateId)
          : ((item as { Description?: string }).Description?.trim() || 'Outros');
      if (!map.has(title)) {
        map.set(title, []);
      }
      map.get(title)!.push(item);
    }
    return Array.from(map.entries()).map(([title, groupItems]) => ({ title, items: groupItems }));
  }

  private appendGeneratedPdfToItems(items: CreateChecklistItem[]): void {
    if (!this.generatedPdfBase64) {
      return;
    }
    const pdfDataUrl = this.generatedPdfBase64.startsWith('data:')
      ? this.generatedPdfBase64
      : `data:application/pdf;base64,${this.generatedPdfBase64}`;
    items.push({
      checklistTemplateItemId: T.documentPdf,
      photoUrl: pdfDataUrl,
      itemName: 'Documento Pdf',
      itemType: 'Document'
    });
  }

  private appendAccessoriesToItems(
    items: CreateChecklistItem[],
    accessoriesData: Record<string, string>
  ): void {
    for (const group of this.accessoryGroups) {
      for (const field of group.items) {
        const value = accessoriesData[field.key];
        if (value === undefined || value === null || value === '') {
          continue;
        }
        const condition = mapSnaToCondition(value);
        items.push({
          checklistTemplateItemId: field.templateId,
          condition,
          observations: `${field.label}: ${condition}`
        });
      }
    }
  }

  /**
   * Mapeia fotos das Sections
   */
  private mapPhotosFromSections(sections: ChecklistSectionDto[]): void {
    const photosSection = sections.find(s => 
      s.SectionType === 'Photos' && s.SectionName.includes('Fotos por Lado')
    );
    
    const photosRequired: Record<VehicleSide, string | null> = {
      frente: null,
      direita: null,
      esquerda: null,
      cima: null,
      traseira: null
    };

    const photoDetails: { side: VehicleSide; file?: File; url?: string; damageType?: DamageType; note?: string }[] = [];

    if (photosSection && photosSection.Items) {
      photosSection.Items.forEach(item => {
        if (item.ItemType === 'Photo' && item.PhotoUrl && item.PhotoSide) {
          const side = this.mapPhotoSideToVehicleSide(item.PhotoSide);
          if (side) {
            photosRequired[side] = item.PhotoUrl;
            photoDetails.push({
              side: side,
              url: item.PhotoUrl,
              note: item.Observations || undefined
            });
          }
        }
      });
    }

    this.photosRequired = photosRequired;
    this.photoDetails = photoDetails;
  }

  /**
   * Mapeia PhotoSide da API para VehicleSide
   */
  private mapPhotoSideToVehicleSide(photoSide: string): VehicleSide | null {
    const mapping: { [key: string]: VehicleSide } = {
      'Front': 'frente',
      'Right': 'direita',
      'Left': 'esquerda',
      'Top': 'cima',
      'Back': 'traseira'
    };
    return mapping[photoSide] || null;
  }

  /**
   * Busca um valor de item por template ID nas sections
   */
  private findItemValueByTemplateId(sections: ChecklistSectionDto[], templateId: number): string | null {
    if (!sections || !Array.isArray(sections)) {
      return null;
    }
    
    for (const section of sections) {
      if (section.Items && Array.isArray(section.Items)) {
        const item = section.Items.find(i => i.ChecklistTemplateItemId === templateId);
        if (item) {
          if (item.TextValue) {
            return item.TextValue;
          }
        }
      }
    }
    
    return null;
  }

  /**
   * Busca um valor numérico de item por template ID nas sections
   */
  private findItemNumberValueByTemplateId(sections: ChecklistSectionDto[], templateId: number): number | null {
    if (!sections || !Array.isArray(sections)) {
      return null;
    }
    
    for (const section of sections) {
      if (section.Items && Array.isArray(section.Items)) {
        const item = section.Items.find(i => i.ChecklistTemplateItemId === templateId);
        if (item) {
          if (item.NumberValue !== null && item.NumberValue !== undefined) {
            return item.NumberValue;
          }
        }
      }
    }
    
    return null;
  }

  /**
   * Busca um valor de item por nome em todas as sections
   */
  private findItemValue(sections: ChecklistSectionDto[], itemName: string): string | null {
    for (const section of sections) {
      const item = section.Items?.find(i => i.ItemName === itemName);
      if (item) {
        return item.TextValue || item.NumberValue?.toString() || item.Value || null;
      }
    }
    return null;
  }

  /**
   * Mapeia TireCondition da API para o tipo esperado
   */
  private mapTireCondition(tireCondition: string | null): 'Bom' | 'Medio' | 'Ruim' | null {
    if (!tireCondition) return null;
    const normalized = tireCondition.toLowerCase();
    if (normalized.includes('bom')) return 'Bom';
    if (normalized.includes('medio') || normalized.includes('médio')) return 'Medio';
    if (normalized.includes('ruim')) return 'Ruim';
    return null;
  }

  /**
   * Mapeia FuelLevel da API para o tipo esperado
   */
  private mapFuelLevel(fuelLevel: string | null): 'reserva' | '1/4' | '1/2' | '3/4' | 'cheio' | null {
    if (!fuelLevel) return null;
    const normalized = fuelLevel.toLowerCase().trim();
    if (normalized === 'reserva') return 'reserva';
    if (normalized === '1/4' || normalized === '1/4') return '1/4';
    if (normalized === '1/2' || normalized === '1/2') return '1/2';
    if (normalized === '3/4' || normalized === '3/4') return '3/4';
    if (normalized === 'cheio') return 'cheio';
    return null;
  }

  /**
   * Abre imagem em nova aba para visualização
   */
  /**
   * Verifica se um item é o PDF anexo do checklist (template ID 71)
   */
  isPdfItem(item: ChecklistItemDetailDto): boolean {
    if (!item) {
      return false;
    }
    return isChecklistPdfAttachment(item.ChecklistTemplateItemId, item.PhotoUrl);
  }

  /**
   * Abre o PDF do checklist em uma nova aba
   * Suporta tanto data URL base64 quanto URL direta (Azure Blob Storage)
   */
  downloadPdf(item: ChecklistItemDetailDto): void {
    if (!item.PhotoUrl || !this.isPdfItem(item)) {
      this.errorService.showError('Erro: Item não é um PDF válido', 'error', true, 5000);
      return;
    }

    const photoUrl = item.PhotoUrl;

    // Verificar se é uma URL direta (começa com http:// ou https://) ou data URL base64
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      // É uma URL direta - abrir através do backend (evita CORS)
      this.openPdfFromUrl(photoUrl);
    } else if (photoUrl.startsWith('data:application/pdf;base64,')) {
      // É um data URL base64 - abrir diretamente
      this.openPdfFromBase64(photoUrl);
    } else {
      this.errorService.showError('Formato de URL do PDF não suportado', 'error', true, 5000);
    }
  }

  /**
   * Abre o PDF em uma nova aba usando a URL do blob diretamente
   */
  private openPdfFromUrl(url: string): void {
    // Usar diretamente a URL do blob que vem do item
    // Se houver problemas de CORS, o backend precisa estar configurado corretamente
    window.open(url, '_blank');
  }

  /**
   * Abre o PDF a partir de um data URL base64 em uma nova aba
   */
  private openPdfFromBase64(dataUrl: string): void {
    try {
      // Abrir diretamente o data URL em nova aba
      window.open(dataUrl, '_blank');
    } catch (error) {
      this.errorService.showError(
        'Erro ao abrir PDF: ' + (error instanceof Error ? error.message : 'Erro desconhecido'),
        'error',
        true,
        5000
      );
    }
  }

  openImageModal(imageUrl: string): void {
    window.open(imageUrl, '_blank');
  }

  /**
   * Formata o status para exibição
   */
  formatStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Draft': 'Rascunho',
      'InProgress': 'Em Progresso',
      'Completed': 'Concluído',
      'Cancelled': 'Cancelado'
    };
    return statusMap[status] || status;
  }

  /** Letra da etapa (A, B, C…) conforme OrderIndex da seção no template. */
  getSectionLetter(orderIndex: number): string {
    const index = Math.max(1, orderIndex) - 1;
    return String.fromCharCode(65 + (index % 26));
  }

  /** Ícone Font Awesome da seção na visualização. */
  getSectionIcon(section: ChecklistSectionDto): string {
    const name = (section.SectionName || '').toLowerCase();
    const type = section.SectionType || '';
    if (name.includes('veículo') || section.OrderIndex === 1) {
      return 'fa-motorcycle';
    }
    if (name.includes('condutor') || section.OrderIndex === 2) {
      return 'fa-user';
    }
    if (name.includes('verifica') || type === 'CriticalItems') {
      return 'fa-clipboard-check';
    }
    if (type === 'Photos' || name.includes('foto')) {
      return name.includes('detalh') ? 'fa-search-plus' : 'fa-camera';
    }
    if (type === 'Accessories' || name.includes('acessório')) {
      return 'fa-tools';
    }
    if (name.includes('observa')) {
      return 'fa-comment-alt';
    }
    if (name.includes('assinatura')) {
      return 'fa-signature';
    }
    if (type === 'Document' || name.includes('documento')) {
      return 'fa-file-pdf';
    }
    return 'fa-list-alt';
  }

  getStatusChipClass(status: string): string {
    const map: Record<string, string> = {
      Completed: 'status-chip--completed',
      Draft: 'status-chip--draft',
      InProgress: 'status-chip--progress',
      Cancelled: 'status-chip--cancelled'
    };
    return map[status] || 'status-chip--default';
  }

  getStatusIcon(status: string): string {
    const map: Record<string, string> = {
      Completed: 'fa-check-circle',
      Draft: 'fa-pencil-alt',
      InProgress: 'fa-spinner',
      Cancelled: 'fa-times-circle'
    };
    return map[status] || 'fa-info-circle';
  }

  /** Ícone do campo na visualização (por template ou tipo). */
  getViewItemIcon(item: ChecklistItemDetailDto): string {
    const id = item.ChecklistTemplateItemId;
    const T = CHECKLIST_TEMPLATE_IDS;
    if (id === T.plate) return 'fa-id-card';
    if (id === T.model) return 'fa-motorcycle';
    if (id === T.odometer || id === T.panelKm) return 'fa-tachometer-alt';
    if (id === T.checklistDate) return 'fa-calendar-day';
    if (id === T.checklistTime) return 'fa-clock';
    if (id === T.driverName || id === T.signatureName) return 'fa-user';
    if (id === T.driverCpf || id === T.signatureCpf) return 'fa-id-card';
    if (id === T.driverPhone || id === T.signaturePhone) return 'fa-phone';
    if (id === T.signatureEmail) return 'fa-envelope';
    if (id === T.tireCondition) return 'fa-circle';
    if (id === T.fuelLevel) return 'fa-gas-pump';
    if (id === T.isDirty) return 'fa-spray-can';
    if (id === T.observations) return 'fa-comment-dots';
    if (item.ItemType === 'Number') return 'fa-hashtag';
    if (item.ItemType === 'Boolean') return 'fa-toggle-on';
    if (item.ItemType === 'Select') return 'fa-list';
    return 'fa-tag';
  }

  /** Valor formatado para exibição na visualização. */
  getViewItemDisplayValue(item: ChecklistItemDetailDto): string {
    if (item.TextValue) {
      return item.TextValue;
    }
    if (item.NumberValue !== null && item.NumberValue !== undefined) {
      const unit = item.Unit ? ` ${item.Unit}` : '';
      return `${item.NumberValue.toLocaleString('pt-BR')}${unit}`;
    }
    if (item.BooleanValue !== null && item.BooleanValue !== undefined) {
      return item.BooleanValue ? 'Sim' : 'Não';
    }
    if (item.Condition) {
      return item.Condition;
    }
    return '';
  }

  isConditionBadgeValue(condition: string): boolean {
    return ['Bom', 'Médio', 'Ruim', 'Cheio', 'Reserva', '1/4', '1/2', '3/4'].includes(condition);
  }

  /**
   * Formata o status da section para exibição
   */
  formatSectionStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Pending': 'Pendente',
      'Completed': 'Concluído',
      'Failed': 'Falhou',
      'InProgress': 'Em Progresso'
    };
    return statusMap[status] || status;
  }

  /**
   * Formata data e hora para exibição
   */
  formatDateTime(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Data inválida';
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Data inválida';
    }
  }

  /**
   * Mapeia PhotoSide para português
   */
  mapPhotoSide(photoSide: string | null): string {
    if (!photoSide) return '';
    const mapping: { [key: string]: string } = {
      'Front': 'Frontal',
      'Right': 'Direito',
      'Left': 'Esquerdo',
      'Top': 'Superior',
      'Back': 'Traseira'
    };
    return mapping[photoSide] || photoSide;
  }

  /**
   * Verifica se há observações nos items
   */
  hasObservations(items: any[]): boolean {
    return items?.some(item => item.Observations) || false;
  }

  /**
   * Remove seções duplicadas, mantendo apenas uma por OrderIndex
   * Se houver múltiplas seções com o mesmo OrderIndex, mantém a que tem mais itens válidos
   */
  getUniqueSections(sections: ChecklistSectionDto[] | undefined): ChecklistSectionDto[] {
    if (!sections || sections.length === 0) return [];
    
    const sectionsMap = new Map<number, ChecklistSectionDto>();
    
    sections.forEach(section => {
      const existingSection = sectionsMap.get(section.OrderIndex);
      
      if (!existingSection) {
        // Primeira seção com este OrderIndex
        sectionsMap.set(section.OrderIndex, section);
      } else {
        // Já existe uma seção com este OrderIndex
        // Manter a que tem mais itens válidos
        const existingItemsCount = this.getValidItems(existingSection.Items).length;
        const currentItemsCount = this.getValidItems(section.Items).length;
        
        if (currentItemsCount > existingItemsCount) {
          sectionsMap.set(section.OrderIndex, section);
        } else if (currentItemsCount === existingItemsCount && section.Items && section.Items.length > 0) {
          // Se tiverem a mesma quantidade de itens válidos, manter a que tem mais itens no total
          const existingTotalItems = existingSection.Items?.length || 0;
          const currentTotalItems = section.Items?.length || 0;
          
          if (currentTotalItems > existingTotalItems) {
            sectionsMap.set(section.OrderIndex, section);
          }
        }
      }
    });
    
    // Converter Map para Array e ordenar por OrderIndex
    return Array.from(sectionsMap.values()).sort((a, b) => a.OrderIndex - b.OrderIndex);
  }

  /**
   * Filtra items vazios (sem nome ou sem valor)
   */
  getValidItems(items: ChecklistItemDetailDto[] | undefined): ChecklistItemDetailDto[] {
    if (!items || items.length === 0) return [];
    
    const validItems = items.filter(item => {
      // SEMPRE incluir PDF do checklist (template ID 71) se tiver PhotoUrl
      if (isChecklistPdfAttachment(item.ChecklistTemplateItemId, item.PhotoUrl)) {
        return true;
      }
      
      // SEMPRE incluir fotos detalhadas (itens com photoSide e photoUrl) - não precisa de ItemName
      if (item.PhotoSide && item.PhotoUrl) {
        return true;
      }
      
      // Filtrar items vazios (sem nome ou com nome vazio) - mas só se não for foto/PDF
      if (!item.ItemName || item.ItemName.trim() === '') return false;
      
      // Incluir items que têm algum valor preenchido
      return item.TextValue || item.NumberValue !== null || item.BooleanValue !== null || 
             item.Condition || item.PhotoUrl || item.IsCompleted;
    });
    
    return validItems;
  }

  /**
   * Prepara os dados da tabela para uma seção
   */
  getTableDataForSection(section: ChecklistSectionDto): any[] {
    return this.getValidItems(section.Items).map(item => ({
      itemName: item.ItemName || '-',
      description: item.Description || '',
      value: this.formatItemValueForTable(item),
      status: item.IsCompleted ? 'Concluído' : 'Pendente',
      observations: item.Observations || '-',
      condition: item.Condition || null,
      itemType: item.ItemType,
      isCompleted: item.IsCompleted
    }));
  }

  /**
   * Obtém as colunas da tabela para uma seção
   */
  getTableColumnsForSection(section: ChecklistSectionDto): { titulo: string, label: string, formatter?: (value: any) => string }[] {
    const columns: { titulo: string, label: string, formatter?: (value: any) => string }[] = [
      { titulo: 'Item', label: 'itemName' },
      { titulo: 'Valor', label: 'value' },
      { titulo: 'Status', label: 'status', formatter: (value) => value }
    ];

    if (this.hasObservations(section.Items)) {
      columns.push({ titulo: 'Observações', label: 'observations' });
    }

    return columns;
  }

  /**
   * Formata o valor do item para exibição na tabela
   */
  formatItemValueForTable(item: ChecklistItemDetailDto): string {
    if (item.TextValue) return item.TextValue;
    if (item.NumberValue !== null && item.NumberValue !== undefined) {
      return item.NumberValue.toString() + (item.Unit ? ` ${item.Unit}` : '');
    }
    if (item.BooleanValue !== null) {
      return item.BooleanValue ? 'Sim' : 'Não';
    }
    if (item.Condition) {
      return item.Condition;
    }
    return '-';
  }

  next(): void { if (this.step < 6) this.step = (this.step + 1) as Step; }
  prev(): void { if (this.step > 1) this.step = (this.step - 1) as Step; }

  canAdvance(current: Step): boolean {
    // Para testes, permitir navegação livre sem validações obrigatórias
    return true;
  }

  advance(): void {
    if (this.canAdvance(this.step)) this.next();
    else this.touchCurrentStep();
  }

  goTo(target: Step): void {
    if (target < this.step) { this.step = target; return; }
    // Only allow jumping forward if all previous steps are valid
    for (let s = 1 as Step; s < target; s = (s + 1) as Step) {
      if (!this.canAdvance(s)) { this.step = s; this.touchStep(s); return; }
    }
    this.step = target;
  }

  get progressPercent(): number {
    return Math.round(((this.step - 1) / (this.totalSteps - 1)) * 100);
  }

  private touchCurrentStep(): void { this.touchStep(this.step); }
  private touchStep(s: Step): void {
    if (s === 1) this.vehicleForm.markAllAsTouched();
    if (s === 2) this.conditionsForm.markAllAsTouched();
  }

  onPlatePhotoSelected(file: File): void {
    // placeholder para integração com modal de captura
  }

onAddDetail(sideValue: string, damageValue: string, note: string, file?: File | null): void {
    if (!file) {
      this.errorService.showWarningMessage('Por favor, tire uma foto antes de adicionar.');
      return;
    }
    const side = sideValue as VehicleSide;
    const damageType = Number(damageValue) as DamageType;
    // Nota: Esta função não é async, então não podemos usar await aqui
    // Mas o addDetailPhoto agora retorna Promise, então vamos tratar como fire-and-forget
    this.addDetailPhoto(side, file, damageType, note).catch(error => {
      // Erro silencioso - já tratado no método addDetailPhoto
    });
    
    // Limpar campos do formulário após adicionar
    const sideSelect = document.querySelector('select[ng-reflect-name="side"]') as HTMLSelectElement;
    const noteInput = document.querySelector('input[placeholder="Descreva o problema..."]') as HTMLInputElement;
    if (sideSelect) sideSelect.value = '';
    if (noteInput) noteInput.value = '';
  }

  openCameraForPlate(): void {
    // Verificar se o navegador suporta getUserMedia
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      this.openFileInput();
      return;
    }
    
    // Verificar se há câmeras disponíveis
    navigator.mediaDevices.enumerateDevices()
    .then(devices => {
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      if (videoDevices.length === 0) {
        this.errorService.showError(
          'Nenhuma câmera encontrada. Use o botão de upload de arquivo.',
          'warning',
          true,
          5000
        );
        this.openFileInput();
        return;
      }
      
      // Abrir modal da câmera
      this.openCameraModal();
    })
    .catch(error => {
      // Tentar abrir modal mesmo assim
      this.openCameraModal();
    });
  }

  openFileInput(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.detectPlateFromImage(file);
      }
    };
    
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  openCameraModal(): void {
    // Criar modal de câmera
    const modalHTML = `
      <div id="cameraModalBackdrop" style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background: rgba(0,0,0,0.8) !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        z-index: 999999 !important;
      ">
        <div style="
          background: white !important;
          width: 600px !important;
          max-width: 90vw !important;
          border-radius: 10px !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3) !important;
          overflow: hidden !important;
          margin: 20px !important;
        ">
          <div style="
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            padding: 12px 16px !important;
            border-bottom: 1px solid #e5e7eb !important;
            background: white !important;
          ">
            <h3 style="margin: 0 !important; color: #1f2937 !important; font-size: 18px !important;">Capturar Foto da Placa</h3>
            <button id="closeCameraModal" style="
              background: transparent !important;
              border: none !important;
              font-size: 20px !important;
              cursor: pointer !important;
              padding: 4px 8px !important;
              color: #64748b !important;
            ">×</button>
          </div>
          <div style="padding: 16px !important; background: white !important;">
            <div id="cameraStatus" style="
              text-align: center !important;
              padding: 20px !important;
              color: #64748b !important;
              font-size: 14px !important;
            ">
              <i class="fas fa-spinner fa-spin" style="margin-right: 8px !important;"></i>
              Iniciando câmera...
            </div>
            <video id="cameraVideo" style="
              width: 100% !important;
              height: 300px !important;
              background: #000 !important;
              border-radius: 6px !important;
              display: none !important;
            " autoplay muted playsinline></video>
            <div id="cameraControls" style="
              margin-top: 12px !important;
              display: none !important;
              justify-content: center !important;
              gap: 10px !important;
            ">
              <button id="capturePhoto" style="
                background: #2b3a67 !important;
                color: white !important;
                border: none !important;
                padding: 12px 24px !important;
                border-radius: 6px !important;
                cursor: pointer !important;
                font-size: 16px !important;
              ">
                <i class="fas fa-camera"></i> Capturar Foto
              </button>
              <button id="switchCamera" style="
                background: #6b7280 !important;
                color: white !important;
                border: none !important;
                padding: 12px 24px !important;
                border-radius: 6px !important;
                cursor: pointer !important;
                font-size: 16px !important;
              ">
                <i class="fas fa-sync-alt"></i> Trocar Câmera
              </button>
            </div>
            <div id="cameraError" style="
              display: none !important;
              text-align: center !important;
              padding: 20px !important;
              color: #dc2626 !important;
              background: #fef2f2 !important;
              border-radius: 6px !important;
              margin-top: 12px !important;
            ">
              <i class="fas fa-exclamation-triangle" style="margin-right: 8px !important;"></i>
              Erro ao acessar a câmera. Verifique as permissões.
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    // Configurar eventos
    document.getElementById('closeCameraModal')?.addEventListener('click', () => this.closeCameraModal());
    document.getElementById('capturePhoto')?.addEventListener('click', () => this.capturePhoto());
    document.getElementById('switchCamera')?.addEventListener('click', () => this.switchCamera());
    document.getElementById('cameraModalBackdrop')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.closeCameraModal();
    });
    
    // Sempre iniciar pela câmera traseira
    this.plateCameraFacingMode = 'environment';
    this.plateCurrentDeviceIndex = 0;

    // Iniciar câmera
    void this.startCamera();
  }

  private videoElement?: HTMLVideoElement;
  private stream?: MediaStream;
  private plateCameraFacingMode: 'user' | 'environment' = 'environment';
  private plateVideoInputDevices: MediaDeviceInfo[] = [];
  private plateCurrentDeviceIndex = 0;
  private findRearCameraIndex(devices: MediaDeviceInfo[]): number {
    if (!devices || devices.length === 0) return 0;
    const rearIndex = devices.findIndex(d => /rear|back|traseira|environment/i.test((d.label || '').toLowerCase()));
    return rearIndex >= 0 ? rearIndex : 0;
  }

  async startCamera(forceFacingMode: boolean = false): Promise<void> {
    const video = document.getElementById('cameraVideo') as HTMLVideoElement;
    const status = document.getElementById('cameraStatus');
    const controls = document.getElementById('cameraControls');
    const error = document.getElementById('cameraError');
    
    if (!video) return;
    
    this.videoElement = video;
    
    // Mostrar status de carregamento
    if (status) status.style.display = 'block';
    if (controls) controls.style.display = 'none';
    if (error) error.style.display = 'none';

    // Garante refresh da lista de câmeras após permissão.
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.plateVideoInputDevices = devices.filter(device => device.kind === 'videoinput');
      if (this.plateCurrentDeviceIndex >= this.plateVideoInputDevices.length) {
        this.plateCurrentDeviceIndex = 0;
      }
      if (!forceFacingMode) {
        this.plateCurrentDeviceIndex = this.findRearCameraIndex(this.plateVideoInputDevices);
      }
    } catch {
      this.plateVideoInputDevices = [];
      this.plateCurrentDeviceIndex = 0;
    }

    const preferredConstraints: MediaStreamConstraints = {
      video: {
        facingMode: forceFacingMode
          ? { exact: this.plateCameraFacingMode }
          : { exact: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 }
      }
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(preferredConstraints);
      this.stream = stream;
      video.srcObject = stream;
      video.onloadedmetadata = () => {
        void video.play();
        if (status) status.style.display = 'none';
        if (controls) controls.style.display = 'flex';
        video.style.display = 'block';
      };
    } catch {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: forceFacingMode ? this.plateCameraFacingMode : { ideal: 'environment' }
          }
        });
        this.stream = stream;
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          void video.play();
          if (status) status.style.display = 'none';
          if (controls) controls.style.display = 'flex';
          video.style.display = 'block';
        };
      } catch {
        try {
          if (this.plateVideoInputDevices.length > 0) {
            const rearIndex = this.findRearCameraIndex(this.plateVideoInputDevices);
            const stream = await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: { exact: this.plateVideoInputDevices[rearIndex].deviceId }
              }
            });
            this.stream = stream;
            video.srcObject = stream;
            video.onloadedmetadata = () => {
              void video.play();
              if (status) status.style.display = 'none';
              if (controls) controls.style.display = 'flex';
              video.style.display = 'block';
            };
            return;
          }

          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          this.stream = stream;
          video.srcObject = stream;
          video.onloadedmetadata = () => {
            void video.play();
            if (status) status.style.display = 'none';
            if (controls) controls.style.display = 'flex';
            video.style.display = 'block';
          };
        } catch {
          if (status) status.style.display = 'none';
          if (error) error.style.display = 'block';
          if (controls) controls.style.display = 'none';
        }
      }
    }
  }

  switchCamera(): void {
    const cameraCount = this.plateVideoInputDevices.length;
    if (cameraCount <= 1) {
      // Fallback por facingMode quando o browser não expõe vários devices.
      this.plateCameraFacingMode = this.plateCameraFacingMode === 'environment' ? 'user' : 'environment';
      this.errorService.showInfoMessage('Tentando alternar câmera por modo frontal/traseira...');
    } else {
      this.plateCurrentDeviceIndex = (this.plateCurrentDeviceIndex + 1) % cameraCount;
      this.plateCameraFacingMode = this.plateCameraFacingMode === 'environment' ? 'user' : 'environment';
    }

    // Parar stream atual
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    
    // Reiniciar câmera
    void this.startCamera(true);
  }

  capturePhoto(): void {
    if (!this.videoElement) return;
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    // Configurar canvas com as dimensões do vídeo
    canvas.width = this.videoElement.videoWidth;
    canvas.height = this.videoElement.videoHeight;
    
    // Capturar frame do vídeo
    context.drawImage(this.videoElement, 0, 0);
    
    // Converter para PNG base64
    const pngDataUrl = canvas.toDataURL('image/png');
    
    // Converter base64 para File
    const byteString = atob(pngDataUrl.split(',')[1]);
    const mimeString = pngDataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], 'plate-photo.png', { type: 'image/png' });
    this.closeCameraModal();
    this.detectPlateFromImage(file);
  }

  closeCameraModal(): void {
    // Parar stream da câmera
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    
    // Remover modal
    const modal = document.getElementById('cameraModalBackdrop');
    if (modal) {
      modal.remove();
    }
  }

  canCancelChecklistFromView(): boolean {
    const status = this.checklistDetail?.Status || '';
    const normalized = status.trim().toLowerCase();
    return normalized !== 'cancelled' && normalized !== 'cancelado';
  }

  /** Cancela o checklist na visualização (POST /checklist/{id}/cancel). */
  cancelChecklistFromView(): void {
    if (!this.id || !this.checklistDetail || !this.canCancelChecklistFromView()) {
      return;
    }

    const plate = this.getPlateFromItems(this.checklistDetail) || this.checklistDetail.VehiclePlate;

    this.confirmationService.confirmCancelChecklist(this.id, plate || undefined).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.isCancellingChecklist = true;
      this.service.cancelChecklist(this.id!).subscribe({
        next: () => {
          this.isCancellingChecklist = false;
          this.errorService.showSuccessMessage('Checklist cancelado com sucesso.');
          this.checklistDetail!.Status = 'Cancelled';
          this.router.navigate(['/checklist']);
        },
        error: (err) => {
          this.isCancellingChecklist = false;
          this.errorService.handleHttpError(err, 'Erro ao cancelar o checklist.');
        }
      });
    });
  }

  /**
   * Cancela a criação do checklist e retorna para a lista
   */
  cancelChecklist(): void {
    if (confirm('Tem certeza que deseja cancelar a criação do checklist? Todos os dados preenchidos serão perdidos.')) {
      if (this.contractIdFrom) {
        this.router.navigate(['/contracts', this.contractIdFrom, 'view']);
      } else {
        this.router.navigate(['/checklist']);
      }
    }
  }

  showPlateConfirmationModal(detectedPlate: string): void {
    const modalHTML = `
      <div id="plateConfirmationModal" style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background: rgba(0,0,0,0.7) !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        z-index: 999999 !important;
      ">
        <div style="
          background: white !important;
          width: 500px !important;
          max-width: 90vw !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3) !important;
          overflow: hidden !important;
          margin: 20px !important;
        ">
          <div style="
            background: linear-gradient(135deg, #2b3a67 0%, #3a4d56 100%) !important;
            padding: 24px !important;
            text-align: center !important;
            color: white !important;
          ">
            <i class="fas fa-check-circle" style="font-size: 48px !important; margin-bottom: 12px !important; color: #4ade80 !important;"></i>
            <h3 style="margin: 0 0 8px 0 !important; font-size: 20px !important; font-weight: 600 !important;">
              Placa Detectada!
            </h3>
            <p style="margin: 0 !important; font-size: 14px !important; opacity: 0.9 !important;">
              Confirme se a placa está correta
            </p>
          </div>
          <div style="padding: 32px 24px !important; text-align: center !important;">
            <div style="
              background: #f3f4f6 !important;
              border: 2px solid #2b3a67 !important;
              border-radius: 8px !important;
              padding: 24px !important;
              margin-bottom: 24px !important;
            ">
              <label style="
                display: block !important;
                font-size: 12px !important;
                color: #6b7280 !important;
                margin-bottom: 8px !important;
                font-weight: 600 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
              ">Placa Detectada</label>
              <div style="
                font-size: 36px !important;
                font-weight: 700 !important;
                color: #2b3a67 !important;
                letter-spacing: 4px !important;
                font-family: 'Courier New', monospace !important;
              " id="detectedPlateNumber">${detectedPlate}</div>
            </div>
            <div style="
              display: flex !important;
              gap: 12px !important;
              justify-content: center !important;
            ">
              <button id="confirmPlateBtn" style="
                background: #2b3a67 !important;
                color: white !important;
                border: none !important;
                padding: 12px 32px !important;
                border-radius: 8px !important;
                cursor: pointer !important;
                font-size: 16px !important;
                font-weight: 600 !important;
                transition: all 0.2s !important;
                flex: 1 !important;
              " onmouseover="this.style.background='#1e2a4a'" onmouseout="this.style.background='#2b3a67'">
                <i class="fas fa-check"></i> Confirmar
              </button>
              <button id="cancelPlateBtn" style="
                background: #e5e7eb !important;
                color: #374151 !important;
                border: none !important;
                padding: 12px 32px !important;
                border-radius: 8px !important;
                cursor: pointer !important;
                font-size: 16px !important;
                font-weight: 600 !important;
                transition: all 0.2s !important;
                flex: 1 !important;
              " onmouseover="this.style.background='#d1d5db'" onmouseout="this.style.background='#e5e7eb'">
                <i class="fas fa-times"></i> Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    // Configurar eventos
    const confirmBtn = document.getElementById('confirmPlateBtn');
    const cancelBtn = document.getElementById('cancelPlateBtn');
    const modal = document.getElementById('plateConfirmationModal');
    
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        this.confirmDetectedPlate(detectedPlate);
        if (modal) modal.remove();
      });
    }
    
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        if (modal) modal.remove();
        this.isLoadingPlateDetection = false;
      });
    }
    
    // Fechar ao clicar fora
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
          this.isLoadingPlateDetection = false;
        }
      });
    }
  }

  confirmDetectedPlate(detectedPlate: string): void {
    // Preencher o campo de placa
    this.vehicleForm.patchValue({
      plate: detectedPlate
    });
    
    // Buscar automaticamente os dados do veículo e condutor via contrato
    this.service.findVehicleByPlate(detectedPlate).subscribe({
      next: (response) => {
        // Verificar se a resposta tem sucesso e dados
        if (!response.success || !response.data || response.data.length === 0) {
          this.errorService.showError(
            `Placa ${detectedPlate} confirmada, mas nenhum veículo foi encontrado com esta placa no sistema.`,
            'warning',
            true,
            6000
          );
          this.isLoadingPlateDetection = false;
          return;
        }
        
        // Pegar o primeiro veículo do array
        const vehicleData = response.data[0];
        // Verificar se tem RentalInfo (contrato ativo)
        const rentalInfo = vehicleData.RentalInfo;
        
        if (!rentalInfo) {
          // Mesmo sem contrato, preencher dados básicos do veículo
          this.vehicleForm.patchValue({
            model: vehicleData.Model || '',
            year: vehicleData.ModelYear || vehicleData.ManufactureYear || null,
            odometerKm: vehicleData.CurrentMileage || null,
            renavam: vehicleData.Renavam || '',
            chassis: vehicleData.ChassisNumber || ''
          });
          this.errorService.showError(
            `Placa ${detectedPlate} confirmada! Veículo não possui contrato ativo. Apenas os dados básicos do veículo foram preenchidos.`,
            'warning',
            true,
            6000
          );
          this.isLoadingPlateDetection = false;
          return;
        }
        
        // Extrair dados do condutor do RentalInfo.Driver
        const driver = rentalInfo.Driver;
        const vehicleSnapshot = rentalInfo.VehicleSnapshot;
        // Preencher dados do veículo (priorizar VehicleSnapshot, depois dados do veículo)
        this.vehicleForm.patchValue({
          model: vehicleSnapshot?.Model || vehicleData.Model || '',
          year: vehicleSnapshot?.Year || vehicleData.ModelYear || vehicleData.ManufactureYear || null,
          odometerKm: vehicleSnapshot?.CurrentKm || vehicleData.CurrentMileage || null,
          renavam: vehicleSnapshot?.Renavam || vehicleData.Renavam || '',
          chassis: vehicleSnapshot?.Chassis || vehicleData.ChassisNumber || ''
        });
        
        // Preencher dados do condutor (se disponível)
        if (driver) {
          const driverName = driver.Name || '';
          const driverCpf = driver.CpfCnpj || '';
          const driverPhone = driver.Phone || '';
          const driverEmail = driver.Email || '';
          this.vehicleForm.patchValue({
            driverName: driverName,
            driverCpf: driverCpf,
            driverPhone: driverPhone
          });
          
          // Preencher também na assinatura
          this.signatureForm.patchValue({
            name: driverName,
            cpf: driverCpf,
            phone: driverPhone,
            email: driverEmail
          });
          this.errorService.showError(
            `Placa ${detectedPlate} confirmada! Dados do veículo e condutor carregados automaticamente via contrato.`,
            'info',
            true,
            5000
          );
        } else {
          this.errorService.showError(
            `Placa ${detectedPlate} confirmada! Dados do veículo carregados. Dados do condutor não encontrados no contrato.`,
            'warning',
            true,
            6000
          );
        }
        
        this.isLoadingPlateDetection = false;
      },
      error: (error) => {
        // Mesmo sem dados do contrato, a placa foi confirmada
        this.errorService.showError(
          `Erro ao buscar dados do veículo via contrato. Verifique se a API está rodando e se o veículo possui um contrato ativo.`,
          'error',
          true,
          7000
        );
        this.isLoadingPlateDetection = false;
      }
    });
  }

  onPlateFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    if (file) {
      this.detectPlateFromImage(file);
    } else {
    }
  }

  detectPlateFromImage(file: File): void {
    this.isLoadingPlateDetection = true;
    
    const formData = new FormData();
    formData.append('image', file);
    
    // Usar endpoint de detecção de placa do microserviço de operações
    const plateDetectionUrl = `${environment.microservices.operation}/plate-detection`;
    
    fetch(plateDetectionUrl, {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      // Verificar se a detecção foi bem-sucedida (suporta ambos os formatos)
      const detectedPlate = data.PlateNumber || data.plateNumber;
      const isSuccess = data.Success || data.success;
      
      if (isSuccess && detectedPlate) {
        // Mostrar modal de confirmação para o usuário
        this.showPlateConfirmationModal(detectedPlate);
      } else {
        const errorMsg = data.ErrorMessage || data.errorMessage || 'Nenhuma placa foi detectada na imagem. Tente novamente.';
        this.errorService.showError(
          errorMsg,
          'warning',
          true,
          6000
        );
        this.isLoadingPlateDetection = false;
      }
    })
    .catch(error => {
      const plateDetectionEndpoint = `${environment.microservices.operation}/plate-detection`;
      alert(`Erro ao processar a imagem: ${error.message}\n\nVerifique se a API está acessível em:\n${plateDetectionEndpoint}`);
      this.isLoadingPlateDetection = false;
    });
  }

  onFindVehicle(): void {
    const plate = this.vehicleForm.value.plate;
    if (!plate) {
      this.errorService.showError(
        'Por favor, informe a placa do veículo para buscar os dados.',
        'warning',
        true,
        5000
      );
      return;
    }
    
    this.isLoading = true;
    this.service.findVehicleByPlate(plate).subscribe({
      next: (response) => {
        // Verificar se a resposta tem sucesso e dados
        if (!response.success || !response.data || response.data.length === 0) {
          this.errorService.showError(
            `Nenhum veículo encontrado com a placa ${plate}. Verifique se a placa está correta.`,
            'warning',
            true,
            6000
          );
          this.isLoading = false;
          return;
        }
        
        // Pegar o primeiro veículo do array
        const vehicleData = response.data[0];
        // Verificar se tem RentalInfo (contrato ativo)
        const rentalInfo = vehicleData.RentalInfo;
        
        if (!rentalInfo) {
          // Mesmo sem contrato, preencher dados básicos do veículo
      this.vehicleForm.patchValue({
            model: vehicleData.Model || '',
            year: vehicleData.ModelYear || vehicleData.ManufactureYear || null,
            odometerKm: vehicleData.CurrentMileage || null,
            renavam: vehicleData.Renavam || '',
            chassis: vehicleData.ChassisNumber || ''
          });
          this.errorService.showError(
            `Dados básicos do veículo carregados! Veículo não possui contrato ativo.`,
            'warning',
            true,
            6000
          );
          this.isLoading = false;
          return;
        }
        
        // Extrair dados do condutor do RentalInfo.Driver
        const driver = rentalInfo.Driver;
        const vehicleSnapshot = rentalInfo.VehicleSnapshot;
        // Preencher dados do veículo (priorizar VehicleSnapshot, depois dados do veículo)
        this.vehicleForm.patchValue({
          model: vehicleSnapshot?.Model || vehicleData.Model || '',
          year: vehicleSnapshot?.Year || vehicleData.ModelYear || vehicleData.ManufactureYear || null,
          odometerKm: vehicleSnapshot?.CurrentKm || vehicleData.CurrentMileage || null,
          renavam: vehicleSnapshot?.Renavam || vehicleData.Renavam || '',
          chassis: vehicleSnapshot?.Chassis || vehicleData.ChassisNumber || ''
        });
        
        // Preencher dados do condutor (se disponível)
        if (driver) {
          const driverName = driver.Name || '';
          const driverCpf = driver.CpfCnpj || '';
          const driverPhone = driver.Phone || '';
          const driverEmail = driver.Email || '';
          
          this.vehicleForm.patchValue({
            driverName: driverName,
            driverCpf: driverCpf,
            driverPhone: driverPhone
          });
          
          // Preencher também na assinatura
          this.signatureForm.patchValue({
            name: driverName,
            cpf: driverCpf,
            phone: driverPhone,
            email: driverEmail
          });
          
          this.errorService.showError(
            `Dados do veículo e condutor carregados com sucesso!`,
            'info',
            true,
            5000
          );
      } else {
          this.errorService.showError(
            `Dados do veículo carregados! Dados do condutor não encontrados no contrato.`,
            'warning',
            true,
            6000
          );
        }
        
        this.isLoading = false;
      },
      error: (error) => {
        this.errorService.showError(
          `Erro ao buscar dados do veículo. Verifique se a API está rodando e se o veículo possui um contrato ativo.`,
          'error',
          true,
          7000
        );
        this.isLoading = false;
      }
    });
  }

  onRequiredPhotoChange(side: VehicleSide, file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.photosRequired[side] = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  onRequiredPhotoInputChange(side: VehicleSide, event: Event | {target: {files: File[]}}): void {
    let file: File | null = null;
    
    if (event && 'target' in event && event.target) {
      const input = event.target as HTMLInputElement;
      file = input.files?.[0] || null;
    } else if (event && 'target' in event && (event as any).target?.files) {
      file = (event as any).target.files[0] || null;
    }
    
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      this.photosRequired[side] = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  addDetailPhoto(side: VehicleSide, file: File, damageType?: DamageType, note?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const photoDetail = { side, file, url: reader.result as string, damageType, note };
        this.photoDetails.push(photoDetail);
        resolve();
      };
      reader.onerror = () => {
        reject(new Error('Erro ao ler arquivo'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Método auxiliar para abrir câmera e capturar foto obrigatória
   */
  openCameraForRequiredPhoto(side: VehicleSide): void {
    this.openCameraForPhoto((file) => {
      this.onRequiredPhotoChange(side, file);
    });
  }

  /**
   * Método auxiliar para abrir câmera e capturar foto detalhada
   * Adiciona automaticamente após capturar a foto
   */
  openCameraForDetailPhoto(sideValue: string, damageValue: string, noteValue: string): void {
    // Validar se os campos obrigatórios estão preenchidos
    if (!sideValue || !damageValue) {
      this.errorService.showWarningMessage('Por favor, selecione o lado do veículo e o tipo de dano antes de tirar a foto.');
      return;
    }

    // Normalizar o lado para minúsculas (pode vir como "Frente", "Esquerda", etc.)
    const normalizedSide = sideValue.toLowerCase().trim();
    
    // Converter damageValue para número
    // O damageValue pode vir como:
    // - Número (1, 2, 3, etc.) - quando ngValue funciona corretamente
    // - String numérica ("1", "2", "3", etc.)
    // - String de texto ("Arranhado", "Amassado", etc.) - quando pega o texto do option
    let damageTypeNum: number;
    
    if (typeof damageValue === 'number') {
      damageTypeNum = damageValue;
    } else if (typeof damageValue === 'string') {
      // Tentar converter string para número
      const parsed = parseInt(damageValue, 10);
      if (!isNaN(parsed)) {
        damageTypeNum = parsed;
      } else {
        // Se não for número, tentar mapear pelo texto
        const textToNumber: { [key: string]: number } = {
          'Arranhado': 1,
          'Amassado': 2,
          'Piques': 3,
          'Trincado': 4,
          'Quebrado': 5,
          'Falta': 6
        };
        damageTypeNum = textToNumber[damageValue] || NaN;
      }
    } else {
      damageTypeNum = NaN;
    }
    
    // Validar se a conversão foi bem-sucedida
    if (isNaN(damageTypeNum) || damageTypeNum < 1 || damageTypeNum > 6) {
      this.errorService.showErrorMessage('Tipo de dano inválido. Por favor, selecione um tipo válido.');
      return;
    }
    
    // Validar se o lado é válido
    const validSides = ['frente', 'direita', 'esquerda', 'cima', 'traseira'];
    if (!validSides.includes(normalizedSide)) {
      this.errorService.showErrorMessage('Lado do veículo inválido. Por favor, selecione um lado válido.');
      return;
    }
    this.openCameraForPhoto(async (file) => {
      // Adicionar automaticamente após capturar a foto
      const side = normalizedSide as VehicleSide;
      const damageType = damageTypeNum as DamageType;
      const note = noteValue?.trim() || undefined;
      try {
        await this.addDetailPhoto(side, file, damageType, note);
      } catch (error) {
        this.errorService.showErrorMessage('Erro ao processar a foto. Tente novamente.');
      }
      
      // Limpar campos do formulário após adicionar
      setTimeout(() => {
        // Usar querySelector para encontrar e limpar os campos
        const sideSelect = document.querySelector('select[ng-reflect-name="side"]') as HTMLSelectElement;
        const damageSelect = document.querySelector('select[ng-reflect-name="damage"]') as HTMLSelectElement;
        const noteInput = document.querySelector('input[placeholder="Descreva o problema..."]') as HTMLInputElement;
        
        if (sideSelect) {
          sideSelect.selectedIndex = 0; // Resetar para primeira opção
        }
        if (damageSelect) {
          damageSelect.selectedIndex = 0; // Resetar para primeira opção
        }
        if (noteInput) {
          noteInput.value = '';
        }
      }, 100);
    });
  }

  /**
   * Abre a câmera do dispositivo para capturar uma foto
   * @param callback Função que será chamada com o arquivo capturado
   */
  openCameraForPhoto(callback: (file: File) => void | Promise<void>): void {
    // Verificar se o navegador suporta getUserMedia
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      this.openFileInputForPhoto(callback);
      return;
    }
    
    // Verificar se há câmeras disponíveis
    navigator.mediaDevices.enumerateDevices()
    .then(devices => {
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      if (videoDevices.length === 0) {
        this.errorService.showWarningMessage('Nenhuma câmera encontrada. Use o botão de upload de arquivo.');
        this.openFileInputForPhoto(callback);
        return;
      }
      
      // Abrir modal da câmera genérico
      this.openCameraModalForPhoto(callback);
    })
    .catch(error => {
      // Tentar abrir modal mesmo assim
      this.openCameraModalForPhoto(callback);
    });
  }

  /**
   * Abre seletor de arquivo como fallback
   */
  openFileInputForPhoto(callback: (file: File) => void | Promise<void>): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Força uso da câmera traseira em dispositivos móveis
    input.style.display = 'none';
    
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const result = callback(file);
        if (result instanceof Promise) {
          result.catch(error => console.error('Erro ao processar arquivo:', error));
        }
      }
    };
    
    document.body.appendChild(input);
    input.click();
    setTimeout(() => document.body.removeChild(input), 100);
  }

  /**
   * Abre modal de câmera genérico para capturar foto
   */
  openCameraModalForPhoto(callback: (file: File) => void | Promise<void>): void {
    // Criar modal de câmera
    const modalHTML = `
      <div id="cameraModalBackdropPhoto" style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(0, 0, 0, 0.9) !important;
        z-index: 10000 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
      ">
        <div style="
          width: 90% !important;
          max-width: 600px !important;
          background: white !important;
          border-radius: 8px !important;
          padding: 20px !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 15px !important;
        ">
          <h3 style="margin: 0 0 10px 0 !important;">Capturar Foto</h3>
          <video id="cameraVideoPhoto" autoplay playsinline style="
            width: 100% !important;
            max-height: 400px !important;
            border-radius: 4px !important;
            background: #000 !important;
          "></video>
          <canvas id="cameraCanvasPhoto" style="display: none !important;"></canvas>
          <div style="display: flex !important; gap: 10px !important; justify-content: center !important;">
            <button id="captureBtnPhoto" style="
              padding: 10px 20px !important;
              background: #007bff !important;
              color: white !important;
              border: none !important;
              border-radius: 4px !important;
              cursor: pointer !important;
              font-size: 16px !important;
            ">Capturar</button>
            <button id="cancelCameraBtnPhoto" style="
              padding: 10px 20px !important;
              background: #6c757d !important;
              color: white !important;
              border: none !important;
              border-radius: 4px !important;
              cursor: pointer !important;
              font-size: 16px !important;
            ">Cancelar</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    const video = document.getElementById('cameraVideoPhoto') as HTMLVideoElement;
    const canvas = document.getElementById('cameraCanvasPhoto') as HTMLCanvasElement;
    const captureBtn = document.getElementById('captureBtnPhoto') as HTMLButtonElement;
    const cancelBtn = document.getElementById('cancelCameraBtnPhoto') as HTMLButtonElement;
    const backdrop = document.getElementById('cameraModalBackdropPhoto') as HTMLElement;
    
    let stream: MediaStream | null = null;
    
    // Iniciar câmera
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment' // Câmera traseira em dispositivos móveis
      } 
    })
    .then(mediaStream => {
      stream = mediaStream;
      video.srcObject = mediaStream;
    })
    .catch(error => {
      this.errorService.showError('Erro ao acessar a câmera. Verifique as permissões.');
      this.removeCameraModalForPhoto();
      // Fallback para input file
      this.openFileInputForPhoto(callback);
    });
    
    // Capturar foto
    captureBtn.onclick = () => {
      if (!video || !canvas) return;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
            this.removeCameraModalForPhoto();
            const result = callback(file);
            if (result instanceof Promise) {
              result.catch(error => console.error('Erro ao processar arquivo:', error));
            }
          }
        }, 'image/jpeg', 0.9);
      }
    };
    
    // Cancelar
    cancelBtn.onclick = () => {
      this.removeCameraModalForPhoto();
    };
    
    backdrop.onclick = (e) => {
      if (e.target === backdrop) {
        this.removeCameraModalForPhoto();
      }
    };
  }

  /**
   * Remove o modal de câmera e para o stream
   */
  removeCameraModalForPhoto(): void {
    const backdrop = document.getElementById('cameraModalBackdropPhoto');
    if (backdrop) {
      const video = document.getElementById('cameraVideoPhoto') as HTMLVideoElement;
      if (video && video.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
      backdrop.remove();
    }
  }

  private buildPayload(): Checklist {
    const payload: Checklist = {
      vehicle: {
        plate: this.vehicleForm.value.plate,
        model: this.vehicleForm.value.model,
        year: this.vehicleForm.value.year,
        odometerKm: this.vehicleForm.value.odometerKm || null,
        checklistDate: this.vehicleForm.value.checklistDate || null,
        checklistTime: this.vehicleForm.value.checklistTime
      },
      driver: {
        name: this.vehicleForm.value.driverName,
        cpf: this.vehicleForm.value.driverCpf,
        phone: this.vehicleForm.value.driverPhone
      },
      conditions: this.conditionsForm.value,
      photos: {
        requiredSides: this.photosRequired as any,
        details: this.photoDetails
      },
      accessories: this.accessoriesForm.value,
      observations: this.observationsForm.value,
      signature: this.signatureForm.value
    };
    return payload;
  }

  /**
   * Converte um File ou Blob para base64 data URL
   */
  private async fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Extrai apenas a string base64 pura de um data URL
   * Remove o prefixo "data:image/jpeg;base64," ou similar
   */
  private extractBase64Only(dataUrl: string): string {
    if (!dataUrl) return '';
    
    // Se já for apenas base64 (sem prefixo), retornar como está
    if (!dataUrl.includes(',')) {
      return dataUrl;
    }
    
    // Extrair apenas a parte após a vírgula
    const base64Index = dataUrl.indexOf(',');
    if (base64Index !== -1) {
      return dataUrl.substring(base64Index + 1);
    }
    
    return dataUrl;
  }

  /**
   * Converte uma string data URL para base64 puro (sem prefixo)
   * O backend pode estar esperando apenas a string base64 sem o prefixo data:image/...
   */
  private async ensureBase64(photoUrl: string | null | undefined, extractOnly: boolean = false): Promise<string | null> {
    if (!photoUrl) return null;
    
    // Se já for base64 data URL
    if (photoUrl.startsWith('data:')) {
      if (extractOnly) {
        // Extrair apenas a parte base64 pura
        return this.extractBase64Only(photoUrl);
      }
      return photoUrl;
    }
    
    // Se for URL, tentar buscar e converter
    try {
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      const dataUrl = await this.fileToBase64(blob);
      
      if (extractOnly) {
        return this.extractBase64Only(dataUrl);
      }
      return dataUrl;
    } catch (error) {
      return photoUrl; // Retornar URL original em caso de erro
    }
  }

  /**
   * Constrói itens temporários do checklist para gerar PDF (sem incluir o PDF em si)
   * INCLUI TODAS AS FOTOS: obrigatórias e detalhadas
   */
  private async buildTemporaryItemsForPdf(): Promise<CreateChecklistItem[]> {
    const vehicleData = this.vehicleForm.value;
    const conditionsData = this.conditionsForm.value;
    const observationsData = this.observationsForm.value;
    const signatureData = this.signatureForm.value;
    const accessoriesData = this.accessoriesForm.value;

    const items: CreateChecklistItem[] = [];

    // Mapear o lado do veículo para o formato esperado pela API
    const photoSideMap: { [key: string]: string } = {
      'frente': 'Front',
      'direita': 'Right',
      'esquerda': 'Left',
      'cima': 'Top',
      'traseira': 'Back'
    };

    // Mapear damageType para condition
    const damageTypeToCondition: { [key: number]: string } = {
      1: 'Arranhado',
      2: 'Amassado',
      3: 'Piques',
      4: 'Trincado',
      5: 'Quebrado',
      6: 'Falta'
    };

    // Seção 1: Dados do Veículo
    const dateStr = vehicleData.checklistDate || new Date().toISOString().split('T')[0];
    const timeStr = vehicleData.checklistTime || new Date().toTimeString().slice(0, 5);
    
    items.push(
      { checklistTemplateItemId: T.plate, textValue: vehicleData.plate || '' },
      { checklistTemplateItemId: T.model, textValue: vehicleData.model || '' },
      { checklistTemplateItemId: T.odometer, numberValue: vehicleData.odometerKm || conditionsData.panelKm || 0 },
      { checklistTemplateItemId: T.checklistDate, textValue: dateStr },
      { checklistTemplateItemId: T.checklistTime, textValue: timeStr }
    );

    // Seção 2: Dados do Condutor
    items.push(
      { checklistTemplateItemId: T.driverName, textValue: vehicleData.driverName || '' },
      { checklistTemplateItemId: T.driverCpf, textValue: vehicleData.driverCpf || '' },
      { checklistTemplateItemId: T.driverPhone, textValue: vehicleData.driverPhone || '' }
    );

    // Seção 3: Verificações do Veículo
    const fuelLevelMap: { [key: string]: string } = {
      'reserva': 'Reserva',
      '1/4': '1/4',
      '1/2': '1/2',
      '3/4': '3/4',
      'cheio': 'Cheio'
    };
    const tireConditionMap: { [key: string]: string } = {
      'Bom': 'Bom',
      'Medio': 'Médio',
      'Ruim': 'Ruim'
    };

    items.push(
      { checklistTemplateItemId: T.panelKm, numberValue: conditionsData.panelKm || vehicleData.odometerKm || 0 },
      {
        checklistTemplateItemId: T.tireCondition,
        condition: tireConditionMap[conditionsData.tireCondition] || conditionsData.tireCondition || 'Bom'
      },
      {
        checklistTemplateItemId: T.fuelLevel,
        condition: fuelLevelMap[conditionsData.fuelLevel] || conditionsData.fuelLevel || '1/2'
      },
      { checklistTemplateItemId: T.isDirty, booleanValue: conditionsData.isDirty || false }
    );

    // Seção 4: Fotos por lado do veículo
    for (const side of this.sides) {
      if (this.photosRequired[side]) {
        try {
          const base64Photo = await this.ensureBase64(this.photosRequired[side], false);
          if (base64Photo) {
            items.push({
              checklistTemplateItemId: CHECKLIST_PHOTO_SIDE_TO_TEMPLATE[side],
              photoUrl: base64Photo,
              itemName: `Foto ${side.charAt(0).toUpperCase() + side.slice(1)}`,
              itemType: 'Photo'
            });
          }
        } catch (error) {
        }
      }
    }

    // Seção 5: FOTOS DETALHADAS (Template IDs 47+)
    // Aguardar um pouco para garantir que todos os FileReaders terminaram
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Filtrar apenas fotos que têm URL ou File
    let validPhotoDetails = this.photoDetails.filter(p => p.url || p.file);
    
    // Se ainda houver fotos sem URL, tentar processar os Files diretamente
    const photosWithoutUrl = this.photoDetails.filter(p => !p.url && p.file);
    if (photosWithoutUrl.length > 0) {
      for (const photoDetail of photosWithoutUrl) {
        if (photoDetail.file) {
          try {
            const base64Url = await this.fileToBase64(photoDetail.file);
            if (base64Url) {
              photoDetail.url = base64Url;
            }
          } catch (error) {
          }
        }
      }
      validPhotoDetails = this.photoDetails.filter(p => p.url || p.file);
    }
    
    // Fotos detalhadas: item genérico do template (ID 18), múltiplas instâncias
    for (let idx = 0; idx < validPhotoDetails.length; idx++) {
      const photoDetail = validPhotoDetails[idx];
      let base64Photo: string | null = null;

      if (photoDetail.file) {
        try {
          base64Photo = await this.fileToBase64(photoDetail.file);
        } catch {
          continue;
        }
      } else if (photoDetail.url) {
        try {
          base64Photo = await this.ensureBase64(photoDetail.url, false);
        } catch {
          continue;
        }
      }

      if (base64Photo) {
        const normalizedSide = photoDetail.side ? photoDetail.side.toLowerCase().trim() : null;
        const condition = photoDetail.damageType ? damageTypeToCondition[photoDetail.damageType] : undefined;
        const photoSide = normalizedSide ? photoSideMap[normalizedSide] : undefined;
        const observations = photoDetail.note?.trim() || undefined;

        items.push({
          checklistTemplateItemId: T.detailPhoto,
          condition: condition || 'Danificado',
          photoUrl: base64Photo,
          photoSide: photoSide || 'Front',
          observations,
          itemName: `Foto Detalhada - ${photoDetail.side || 'Lado'} - ${condition || 'Danificado'}`,
          itemType: 'Photo'
        });
      }
    }

    this.appendAccessoriesToItems(items, accessoriesData);

    if (observationsData.notes) {
      items.push({
        checklistTemplateItemId: T.observations,
        textValue: observationsData.notes
      });
    }

    items.push(
      { checklistTemplateItemId: T.signatureName, textValue: signatureData.name || '', itemName: 'Nome' },
      { checklistTemplateItemId: T.signaturePhone, textValue: signatureData.phone || '', itemName: 'Telefone' },
      { checklistTemplateItemId: T.signatureEmail, textValue: signatureData.email || '', itemName: 'E-mail' },
      { checklistTemplateItemId: T.signatureCpf, textValue: signatureData.cpf || '', itemName: 'CPF' }
    );

    if (signatureData.signatureDataUrl) {
      const base64Signature = await this.ensureBase64(signatureData.signatureDataUrl, false);
      if (base64Signature) {
        items.push({
          checklistTemplateItemId: T.signaturePhoto,
          photoUrl: base64Signature,
          itemName: 'Assinatura do Condutor',
          itemType: 'Photo'
        });
      }
    }

    this.appendGeneratedPdfToItems(items);

    return items;
  }

  /**
   * Constrói o payload completo para criar um novo checklist com todos os itens
   */
  private async buildCreatePayload(vehicleId: number = 1, customerId: number = 1): Promise<CreateChecklistRequest> {
    const vehicleData = this.vehicleForm.value;
    const conditionsData = this.conditionsForm.value;
    const observationsData = this.observationsForm.value;
    const signatureData = this.signatureForm.value;
    const accessoriesData = this.accessoriesForm.value;

    // Combinar data e hora do checklist
    const checklistDate = vehicleData.checklistDate 
      ? new Date(`${vehicleData.checklistDate}T${vehicleData.checklistTime || '00:00'}:00`)
      : new Date();

    // Mapear fuelLevel para o formato da API
    const fuelLevelMap: { [key: string]: string } = {
      'reserva': 'Reserva',
      '1/4': '1/4',
      '1/2': '1/2',
      '3/4': '3/4',
      'cheio': 'Cheio'
    };

    // Mapear tireCondition para o formato da API
    const tireConditionMap: { [key: string]: string } = {
      'Bom': 'Bom',
      'Medio': 'Médio',
      'Ruim': 'Ruim'
    };

    // Construir array de itens
    const items: CreateChecklistItem[] = [];

    // Mapear o lado do veículo para o formato esperado pela API (usado em múltiplas seções)
    const photoSideMap: { [key: string]: string } = {
      'frente': 'Front',
      'direita': 'Right',
      'esquerda': 'Left',
      'cima': 'Top',
      'traseira': 'Back'
    };

    // Seção 1: Dados do Veículo
    const dateStr = vehicleData.checklistDate || new Date().toISOString().split('T')[0];
    const timeStr = vehicleData.checklistTime || new Date().toTimeString().slice(0, 5);
    
    items.push(
      { checklistTemplateItemId: T.plate, textValue: vehicleData.plate || '' },
      { checklistTemplateItemId: T.model, textValue: vehicleData.model || '' },
      { checklistTemplateItemId: T.odometer, numberValue: conditionsData.panelKm || vehicleData.odometerKm || 0 },
      { checklistTemplateItemId: T.checklistDate, textValue: dateStr },
      { checklistTemplateItemId: T.checklistTime, textValue: timeStr }
    );

    items.push(
      { checklistTemplateItemId: T.driverName, textValue: vehicleData.driverName || '' },
      { checklistTemplateItemId: T.driverCpf, textValue: vehicleData.driverCpf || '' },
      { checklistTemplateItemId: T.driverPhone, textValue: vehicleData.driverPhone || '' }
    );

    items.push(
      { checklistTemplateItemId: T.panelKm, numberValue: conditionsData.panelKm || vehicleData.odometerKm || 0 },
      {
        checklistTemplateItemId: T.tireCondition,
        condition: tireConditionMap[conditionsData.tireCondition] || conditionsData.tireCondition || 'Bom'
      },
      {
        checklistTemplateItemId: T.fuelLevel,
        condition: fuelLevelMap[conditionsData.fuelLevel] || conditionsData.fuelLevel || '1/2'
      },
      { checklistTemplateItemId: T.isDirty, booleanValue: conditionsData.isDirty || false }
    );

    for (const side of this.sides) {
      if (this.photosRequired[side]) {
        const base64Photo = await this.ensureBase64(this.photosRequired[side], false);
        if (base64Photo) {
          items.push({
            checklistTemplateItemId: CHECKLIST_PHOTO_SIDE_TO_TEMPLATE[side],
            photoUrl: base64Photo
          });
        }
      }
    }

    const damageTypeToCondition: { [key: number]: string } = {
      1: 'Arranhado',
      2: 'Amassado',
      3: 'Piques',
      4: 'Trincado',
      5: 'Quebrado',
      6: 'Falta'
    };

    await new Promise((resolve) => setTimeout(resolve, 500));

    let validPhotoDetails = this.photoDetails.filter((p) => p.url || p.file);
    const photosWithoutUrl = this.photoDetails.filter((p) => !p.url && p.file);
    if (photosWithoutUrl.length > 0) {
      for (const photoDetail of photosWithoutUrl) {
        if (photoDetail.file) {
          try {
            const base64Url = await this.fileToBase64(photoDetail.file);
            if (base64Url) {
              photoDetail.url = base64Url;
            }
          } catch {
            /* ignore */
          }
        }
      }
      validPhotoDetails = this.photoDetails.filter((p) => p.url || p.file);
    }

    for (const photoDetail of validPhotoDetails) {
      let base64Photo: string | null = null;
      if (photoDetail.file) {
        try {
          base64Photo = await this.fileToBase64(photoDetail.file);
        } catch {
          continue;
        }
      } else if (photoDetail.url) {
        try {
          base64Photo = await this.ensureBase64(photoDetail.url, false);
        } catch {
          continue;
        }
      }

      if (base64Photo) {
        const normalizedSide = photoDetail.side ? photoDetail.side.toLowerCase().trim() : null;
        const condition = photoDetail.damageType ? damageTypeToCondition[photoDetail.damageType] : undefined;
        const photoSide = normalizedSide ? photoSideMap[normalizedSide] : undefined;
        const observations = photoDetail.note?.trim() || undefined;
        items.push({
          checklistTemplateItemId: T.detailPhoto,
          condition,
          photoUrl: base64Photo,
          photoSide,
          ...(observations ? { observations } : {})
        });
      }
    }

    this.appendAccessoriesToItems(items, accessoriesData);

    if (observationsData.notes) {
      items.push({
        checklistTemplateItemId: T.observations,
        textValue: observationsData.notes
      });
    }

    items.push(
      { checklistTemplateItemId: T.signatureName, textValue: signatureData.name || '' },
      { checklistTemplateItemId: T.signaturePhone, textValue: signatureData.phone || '' },
      { checklistTemplateItemId: T.signatureEmail, textValue: signatureData.email || '' },
      { checklistTemplateItemId: T.signatureCpf, textValue: signatureData.cpf || '' }
    );

    if (signatureData.signatureDataUrl) {
      const base64Signature = await this.ensureBase64(signatureData.signatureDataUrl, false);
      if (base64Signature) {
        items.push({
          checklistTemplateItemId: T.signaturePhoto,
          photoUrl: base64Signature
        });
      }
    }

    this.appendGeneratedPdfToItems(items);

    const payload: CreateChecklistRequest = {
      vehicleId: vehicleId,
      customerId: customerId,
      checklistTemplateId: 1,
      checklistType: this.selectedChecklistType,
      checklistSubType: 'Vistoria Completa Loca9Motos',
      sector: 'Operações',
      status: 'Vigente',
      checklistDate: checklistDate.toISOString(),
      description: 'Checklist completo de inspeção de motocicleta em 9 etapas',
      mileage: vehicleData.odometerKm || conditionsData.panelKm || 0,
      items: items
    };

    return payload;
  }

  /**
   * Constrói um objeto ChecklistDetailDto temporário a partir dos dados do formulário
   * para gerar o PDF antes de salvar
   */
  private buildTemporaryChecklistData(
    vehicleId: number,
    customerId: number,
    items: CreateChecklistItem[],
    checklistDate: Date
  ): ChecklistDetailDto {
    const vehicleData = this.vehicleForm.value;
    const signatureData = this.signatureForm.value;

    // Construir seções temporárias a partir dos items
    const sections: ChecklistSectionDto[] = [];
    
    // Agrupar items por seção (baseado nos template IDs conhecidos)
    const sectionMap: { [key: string]: ChecklistItemDetailDto[] } = {};
    
    items.forEach((item, index) => {
      let sectionName = 'Geral';
      
      if (item.checklistTemplateItemId) {
        const templateId = item.checklistTemplateItemId;
        if (templateId >= T.plate && templateId <= T.checklistTime) {
          sectionName = 'Dados do Veículo';
        } else if (templateId >= T.driverName && templateId <= T.driverPhone) {
          sectionName = 'Dados do Condutor';
        } else if (templateId >= T.panelKm && templateId <= T.isDirty) {
          sectionName = 'Verificações do Veículo';
        } else if (templateId >= T.photoFront && templateId <= T.photoBack) {
          sectionName = 'Fotos por Lado do Veículo';
        } else if (templateId === T.detailPhoto) {
          sectionName = 'Fotos Detalhadas por Lado';
        } else if (CHECKLIST_ACCESSORY_TEMPLATE_IDS.includes(templateId)) {
          sectionName = 'Checklist de Acessórios / Equipamentos - Motocicleta';
        } else if (templateId === T.observations) {
          sectionName = 'Observações';
        } else if (templateId >= T.signatureName && templateId <= T.signaturePhoto) {
          sectionName = 'Assinatura do Condutor';
        } else if (templateId === T.documentPdf) {
          sectionName = 'Documentos do checklist';
        }
      }
      
      if (!sectionMap[sectionName]) {
        sectionMap[sectionName] = [];
      }
      
      const itemDto: any = {
        ChecklistSectionId: 0,
        ChecklistTemplateItemId: item.checklistTemplateItemId || null,
        ItemName: item.itemName || '',
        ItemType: item.itemType || 'Text',
        Description: null,
        Value: null,
        TextValue: item.textValue || null,
        NumberValue: item.numberValue ?? null,
        BooleanValue: item.booleanValue ?? null,
        Condition: item.condition || null,
        PreviousCondition: null,
        ConditionChanged: false,
        PhotoUrl: item.photoUrl || null,
        PhotoStorageKey: null,
        PhotoSide: item.photoSide || null,
        Observations: item.observations || null,
        ItemId: null,
        Quantity: 0,
        PlannedQuantity: null,
        Unit: null,
        ItemUsed: false,
        ItemUsedAt: null,
        IsVerified: false,
        VerifiedAt: null,
        VerifiedBy: null,
        IsCompleted: item.isCompleted ?? true,
        CompletedAt: null,
        CompletedBy: null,
        OrderIndex: item.orderIndex || index,
        ItemCode: null,
        ItemNameFromStock: null,
        Id: index + 1,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        CreatedBy: null,
        UpdatedBy: null
      };
      
      sectionMap[sectionName].push(itemDto);
    });
    
    // Converter map em array de seções
    Object.keys(sectionMap).forEach((sectionName, index) => {
      sections.push({
        Id: index + 1,
        ChecklistId: 0,
        ChecklistTemplateSectionId: index + 1,
        SectionName: sectionName,
        SectionType: 'Standard',
        Sector: 'Operações',
        Status: 'Completed',
        CompletedAt: new Date().toISOString(),
        CompletedBy: null,
        OrderIndex: index,
        IsRequired: true,
        Items: sectionMap[sectionName],
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        CreatedBy: null,
        UpdatedBy: null
      });
    });

    // Construir ChecklistDetailDto temporário (estende ChecklistItemDto)
    const tempChecklist: any = {
      Id: 0,
      VehicleId: vehicleId,
      CustomerId: customerId,
      ChecklistTemplateId: 3,
      ContractDraftId: null,
      RelatedChecklistId: null,
      ChecklistType: this.selectedChecklistType,
      ChecklistSubType: 'Vistoria Completa',
      Status: 'Vigente',
      ChecklistDate: checklistDate.toISOString(),
      CompletedAt: null,
      CompletedBy: null,
      Mileage: vehicleData.odometerKm || 0,
      DashboardMileage: vehicleData.odometerKm || 0,
      MileageDifference: null,
      ExcessMileage: null,
      FuelLevel: '',
      TireCondition: '',
      IsVehicleClean: false,
      IsVehicleDirty: false,
      ExcessMileageCharge: null,
      DamageCharge: null,
      TotalCharges: null,
      Sector: 'Operações',
      IsMultiSector: false,
      CurrentSector: null,
      DriverName: signatureData.name || '',
      DriverCPF: signatureData.cpf || null,
      DriverEmail: signatureData.email || null,
      DriverPhone: signatureData.phone || null,
      DriverSignature: null,
      Observations: null,
      Step1_VehicleDataCompleted: true,
      Step2_VehicleConditionCompleted: true,
      Step3_PhotosCompleted: true,
      Step4_AccessoriesCompleted: true,
      Step5_ObservationsCompleted: true,
      Step6_SignatureCompleted: true,
      IsCompleted: true,
      VehiclePlate: vehicleData.plate || '',
      CustomerName: null,
      TemplateName: null,
      PreventiveMaintenances: [],
      Sections: sections,
      SectorExecutions: [],
      CreatedAt: new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
      CreatedBy: null,
      UpdatedBy: null
    };

    return tempChecklist;
  }

  async save(): Promise<void> {
    if (this.isEdit && this.id) {
      // TODO: Implementar update quando necessário
      const payload = this.buildPayload();
      this.service.update(this.id, payload).subscribe(() => this.router.navigate(['/checklist']));
    } else {
      // Criar novo checklist usando o template 3 com todos os itens
      this.isLoading = true;
      const vehicleData = this.vehicleForm.value;
      const plate = vehicleData.plate?.trim();

      try {
        let vehicleId = 1;
        let customerId = 1;

        // Se houver placa, buscar o veículo para obter vehicleId e customerId
        if (plate) {
          try {
            const vehicleResponse = await firstValueFrom(this.service.findVehicleByPlate(plate));
            if (vehicleResponse?.success && vehicleResponse?.data && vehicleResponse.data.length > 0) {
              const vehicle = vehicleResponse.data[0];
              vehicleId = vehicle?.Id || vehicle?.id || vehicle?.vehicleId || 1;
              customerId = vehicle?.RentalInfo?.CustomerId || vehicle?.customerId || vehicle?.customer?.id || 1;
            }
          } catch (error) {
          }
        }

        // Se houver assinatura mas PDF ainda não foi gerado, gerar automaticamente antes de salvar
        if (this.signatureForm.value.signatureDataUrl && !this.generatedPdfBlob) {
          try {
            const tempItems = await this.buildTemporaryItemsForPdf();
            const checklistDate = vehicleData.checklistDate 
              ? new Date(`${vehicleData.checklistDate}T${vehicleData.checklistTime || '00:00'}:00`)
              : new Date();
            
            const tempChecklistData = this.buildTemporaryChecklistData(vehicleId, customerId, tempItems, checklistDate);
            const pdfBlob = await this.pdfService.generateChecklistPDF(tempChecklistData);
            // Armazenar o Blob original para upload
            this.generatedPdfBlob = pdfBlob;
            
            // Converter para base64 também (para exibição)
            const pdfBase64 = await this.pdfService.pdfBlobToBase64(pdfBlob);
            this.generatedPdfBase64 = pdfBase64;
          } catch (error) {
            // Continuar mesmo se o PDF falhar
          }
        } else {
        }

        // Construir payload completo com todos os itens
        const payload = await this.buildCreatePayload(vehicleId, customerId);
        
        // Contar fotos
        const photoItems = payload.items?.filter(item => item.photoUrl) || [];
        // Criar checklist com todos os itens em uma única requisição
        this.createChecklist(payload);
      } catch (error) {
        this.isLoading = false;
        this.errorService.showError(
          'Erro ao preparar dados do checklist: ' + (error instanceof Error ? error.message : 'Erro desconhecido'),
          'error',
          true,
          7000
        );
      }
    }
  }

  /**
   * Executa a criação do checklist completo com todos os itens usando o endpoint /full
   */
  private createChecklist(payload: CreateChecklistRequest): void {
    // Log detalhado dos itens com fotos
    const photoItems = payload.items?.filter(item => item.photoUrl) || [];
    
    photoItems.forEach((item, index) => {
      // Processar item com foto
    });
    
    // Log específico das fotos detalhadas no payload (identificadas pela presença de photoSide e checklistTemplateItemId >= 47)
    const detailPhotosInPayload =
      payload.items?.filter(
        (i) => i.photoSide && i.photoUrl && i.checklistTemplateItemId === T.detailPhoto
      ) || [];
    
    const pdfItem = payload.items?.find((i) => i.checklistTemplateItemId === T.documentPdf);
    if (pdfItem) {
      // PDF encontrado no payload
    } else {
      // PDF não encontrado no payload
    }
    
    if (detailPhotosInPayload.length !== this.photoDetails.length) {
      // Discrepância detectada - mas continuar mesmo assim
    }
    detailPhotosInPayload.forEach((item, idx) => {
      // Processar foto detalhada
    });
    
    // Log do payload completo antes de enviar (para comparação com o exemplo)
    
    // Verificar se o payload está no formato correto comparando com o exemplo
    const payloadString = JSON.stringify(payload);
    const hasCorrectStatus = payload.status === 'Vigente';
    const hasCorrectSector = payload.sector === 'Operações';
    const hasCorrectSubType = payload.checklistSubType === 'Vistoria Completa';
    const hasDetailPhotos = detailPhotosInPayload.length > 0;
    const allDetailPhotosHaveRequiredFields = detailPhotosInPayload.every(item => 
      item.checklistTemplateItemId && 
      item.condition && 
      item.photoUrl && 
      item.photoSide
    );
    
    // Regras de checklist de contrato: tipo "Entrega de Veiculo" e setor "Comercial"
    if (this.contractIdFrom) {
      payload.checklistType = 'Entrega de Veiculo';
      payload.sector = 'Comercial';
    }

    // Checklist de contrato: POST /checklist/contract/{id}/b2c/full | Checklist normal: POST /checklist/full
    const createRequest$ = this.contractIdFrom
      ? this.service.createFullForContract(this.contractIdFrom, payload)
      : this.service.createFull(payload);

    createRequest$.subscribe({
      next: (response) => {
        // Verificar se a resposta indica sucesso
        if (response.success) {
          const checklistId = response.data?.id;
          
          if (checklistId) {
            // Verificar se as fotos foram processadas (opcional, não bloqueia o sucesso)
            if (photoItems.length > 0) {
              // Aguardar um pouco e verificar o checklist criado (em background, não bloqueia)
              setTimeout(() => {
                this.service.getById(checklistId, true).subscribe({
                  next: (checklistResponse) => {
                    if (checklistResponse.success && checklistResponse.data) {
                      const sections = checklistResponse.data.Sections || [];
                      let totalPhotosInChecklist = 0;
                      sections.forEach((section: any) => {
                        if (section.Items) {
                          const photosInSection = section.Items.filter((item: any) => item.PhotoUrl).length;
                          totalPhotosInChecklist += photosInSection;
                        }
                      });
                      if (totalPhotosInChecklist < photoItems.length) {
                        this.errorService.showError(
                          'Checklist criado, mas apenas ' + totalPhotosInChecklist + ' de ' + photoItems.length + ' fotos foram salvas. Verifique os logs do backend.',
                          'warning',
                          true,
                          8000
                        );
                      } else {
                        // Todas as fotos foram salvas corretamente
                      }
                    }
                  },
                  error: (err) => {
                  }
                });
              }, 2000);
            }
          }
          
          // Sempre mostrar mensagem de sucesso
          this.isLoading = false;
          const successMessage = response.message || 'Checklist criado com sucesso!';
          this.errorService.showError(
            successMessage,
            'info',
            true,
            3000
          );
          
          // Redirecionar: se veio do contrato, voltar à view do contrato; senão, lista de checklists
          setTimeout(() => {
            if (this.contractIdFrom) {
              this.router.navigate(['/contracts', this.contractIdFrom, 'view']);
            } else {
              this.router.navigate(['/checklist']);
            }
          }, 1000);
        } else {
          // Resposta não indica sucesso
          this.isLoading = false;
          this.errorService.showError(
            'Erro ao criar checklist: ' + (response.message || 'Erro desconhecido'),
            'error',
            true,
            7000
          );
        }
      },
      error: (error) => {
        this.isLoading = false;
        const errorMessage = error?.error?.message || error?.message || 'Erro desconhecido';
        this.errorService.showError(
          'Erro ao criar checklist: ' + errorMessage,
          'error',
          true,
          7000
        );
      }
    });
  }

  /**
   * Busca as seções do checklist e cria os itens
   */
  private fillChecklistSectionsWithItems(checklistId: number, items: CreateChecklistItem[]): void {
    this.service.getById(checklistId, true).subscribe({
      next: (response) => {
        if (response.success && response.data?.Sections) {
          const sections = response.data.Sections;
          // Mapear seções por OrderIndex
          const sectionsMap = new Map<number, any>();
          sections.forEach((section: any) => {
            sectionsMap.set(section.OrderIndex, section);
          });

          // Mapear itens para as seções corretas baseado no checklistTemplateItemId
          // Precisamos mapear cada item para a seção correta
          const itemsBySection: Array<{sectionId: number, item: any}> = [];
          
          items.forEach(item => {
            // Determinar a seção baseado no templateItemId ou itemType
            let sectionIndex: number | undefined;
            
            if (item.checklistTemplateItemId !== undefined) {
              sectionIndex = this.getSectionIndexByTemplateItemId(item.checklistTemplateItemId);
            } else {
              // Para itens sem checklistTemplateItemId (acessórios e fotos detalhadas)
              if (item.itemType === 'Photo' && item.photoUrl) {
                sectionIndex = 5; // Seção 5: Fotos Detalhadas
              } else if (item.itemType === 'Select' && item.conditionValue) {
                sectionIndex = 6; // Seção 6: Acessórios
              }
            }
            
            if (sectionIndex === undefined) {
              return; // Pular itens sem seção definida
            }
            
            const section = sectionsMap.get(sectionIndex);
            
            if (section) {
              // Converter CreateChecklistItem para o formato esperado pela API
              const itemPayload: any = {};
              
              // Incluir checklistTemplateItemId apenas se existir
              if (item.checklistTemplateItemId !== undefined) {
                itemPayload.checklistTemplateItemId = item.checklistTemplateItemId;
              }
              
              // Incluir itemName e itemType para itens sem checklistTemplateItemId
              if (item.itemName !== undefined) itemPayload.itemName = item.itemName;
              if (item.itemType !== undefined) itemPayload.itemType = item.itemType;
              
              if (item.textValue !== undefined) itemPayload.textValue = item.textValue;
              if (item.numberValue !== undefined) itemPayload.numberValue = item.numberValue;
              if (item.booleanValue !== undefined) itemPayload.booleanValue = item.booleanValue;
              if (item.condition !== undefined) itemPayload.condition = item.condition;
              if (item.conditionValue !== undefined) itemPayload.conditionValue = item.conditionValue;
              if (item.photoUrl !== undefined) itemPayload.photoUrl = item.photoUrl;
              if (item.photoSide !== undefined) itemPayload.photoSide = item.photoSide;
              if (item.observations !== undefined) itemPayload.observations = item.observations;
              if (item.orderIndex !== undefined) itemPayload.orderIndex = item.orderIndex;
              if (item.isCompleted !== undefined) itemPayload.isCompleted = item.isCompleted;
              
              itemsBySection.push({
                sectionId: section.Id,
                item: itemPayload
              });
            } else {
            }
          });
          this.createItemsSequentially(itemsBySection, 0, checklistId);
        } else {
          // Tentar buscar seções diretamente
          this.service.getChecklistSections(checklistId).subscribe({
            next: (sectionsResponse: any) => {
              if (sectionsResponse.data && sectionsResponse.data.length > 0) {
                const sectionsMap = new Map<number, any>();
                sectionsResponse.data.forEach((section: any) => {
                  sectionsMap.set(section.orderIndex || section.OrderIndex, section);
                });
                // Processar itens...
                this.isLoading = false;
                alert('Checklist criado, mas houve erro ao processar seções. Redirecionando...');
                this.router.navigate(['/checklist', checklistId, 'view']);
              } else {
                this.isLoading = false;
                alert('Checklist criado, mas não foi possível encontrar as seções. Redirecionando...');
                this.router.navigate(['/checklist', checklistId, 'view']);
              }
            },
            error: (err) => {
              this.isLoading = false;
              alert('Checklist criado, mas houve erro ao buscar seções. Redirecionando...');
              this.router.navigate(['/checklist', checklistId, 'view']);
            }
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        alert('Checklist criado, mas houve erro ao buscar seções. Redirecionando...');
        this.router.navigate(['/checklist', checklistId, 'view']);
      }
    });
  }

  /**
   * Determina o índice da seção baseado no templateItemId
   */
  private getSectionIndexByTemplateItemId(templateItemId: number | undefined): number | undefined {
    if (templateItemId === undefined) {
      return undefined;
    }
    // Seção 1: Dados do Veículo (22-26, 45-46)
    if ([22, 23, 24, 25, 26, 45, 46].includes(templateItemId)) return 1;
    // Seção 2: Dados do Condutor (27-29)
    if ([27, 28, 29].includes(templateItemId)) return 2;
    // Seção 3: Verificações (30-33)
    if ([30, 31, 32, 33].includes(templateItemId)) return 3;
    // Seção 4: Fotos por Lado (34-38)
    if ([34, 35, 36, 37, 38].includes(templateItemId)) return 4;
    // Seção 5: Fotos Detalhadas (47+ para fotos detalhadas)
    if (templateItemId === 47) return 5;
    // Seção 6: Acessórios (47-64)
    if (templateItemId >= 47 && templateItemId <= 64 && templateItemId !== 47) return 6;
    // Seção 7: Observações (39)
    if (templateItemId === 39) return 7;
    // Seção 8: Assinatura (40-44)
    if ([40, 41, 42, 43, 44].includes(templateItemId)) return 8;
    
    // Default: tentar seção 5 para fotos detalhadas
    return 5;
  }

  /**
   * Busca as seções do checklist e preenche todos os itens
   */
  private fillChecklistSections(checklistId: number): void {
    // Primeiro, buscar o checklist com detalhes para obter as seções
    this.service.getById(checklistId, true).subscribe({
      next: (response) => {
        if (response.success && response.data?.Sections) {
          const sections = response.data.Sections;
          // Mapear seções por OrderIndex para facilitar o acesso
          const sectionsMap = new Map<number, any>();
          sections.forEach((section: any) => {
            sectionsMap.set(section.OrderIndex, section);
          });

          // Preencher todas as seções em sequência
          this.fillAllSections(checklistId, sectionsMap);
        } else {
          // Tentar buscar seções diretamente
          this.service.getChecklistSections(checklistId).subscribe({
            next: (sectionsResponse: any) => {
              if (sectionsResponse.data && sectionsResponse.data.length > 0) {
                const sectionsMap = new Map<number, any>();
                sectionsResponse.data.forEach((section: any) => {
                  sectionsMap.set(section.orderIndex || section.OrderIndex, section);
                });
                this.fillAllSections(checklistId, sectionsMap);
              } else {
                this.isLoading = false;
                alert('Checklist criado, mas não foi possível encontrar as seções. Redirecionando...');
                this.router.navigate(['/checklist', checklistId, 'view']);
              }
            },
            error: (err) => {
              this.isLoading = false;
              alert('Checklist criado, mas houve erro ao buscar seções. Redirecionando...');
              this.router.navigate(['/checklist', checklistId, 'view']);
            }
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        alert('Checklist criado, mas houve erro ao buscar seções. Redirecionando...');
        this.router.navigate(['/checklist', checklistId, 'view']);
      }
    });
  }

  /**
   * Preenche todos os itens de todas as seções
   */
  private fillAllSections(checklistId: number, sectionsMap: Map<number, any>): void {
    const allItems: Array<{sectionId: number, item: any}> = [];
    
    // Mapear o lado do veículo para o formato esperado pela API (usado em múltiplas seções)
    const photoSideMap: { [key: string]: string } = {
      'frente': 'Front',
      'direita': 'Right',
      'esquerda': 'Left',
      'cima': 'Top',
      'traseira': 'Back'
    };

    // Seção 1: Dados do Veículo (OrderIndex: 1)
    const section1 = sectionsMap.get(1);
    if (section1) {
      const vehicleData = this.vehicleForm.value;
      const checklistDate = vehicleData.checklistDate || new Date().toISOString().split('T')[0];
      const checklistTime = vehicleData.checklistTime || new Date().toTimeString().slice(0, 5);
      
      allItems.push(
        { sectionId: section1.Id, item: { checklistTemplateItemId: T.plate, itemName: 'Placa do Veículo', itemType: 'Text', textValue: vehicleData.plate || '', orderIndex: 1, isCompleted: true } },
        { sectionId: section1.Id, item: { checklistTemplateItemId: T.model, itemName: 'Modelo do Veículo', itemType: 'Text', textValue: vehicleData.model || '', orderIndex: 2, isCompleted: true } },
        { sectionId: section1.Id, item: { checklistTemplateItemId: T.odometer, itemName: 'Quilometragem', itemType: 'Number', numberValue: vehicleData.odometerKm || 0, orderIndex: 3, isCompleted: true } },
        { sectionId: section1.Id, item: { checklistTemplateItemId: T.checklistDate, itemName: 'Data do Checklist', itemType: 'Text', textValue: checklistDate, orderIndex: 4, isCompleted: true } },
        { sectionId: section1.Id, item: { checklistTemplateItemId: T.checklistTime, itemName: 'Hora do Checklist', itemType: 'Text', textValue: checklistTime, orderIndex: 5, isCompleted: true } }
      );
    }

    // Seção 2: Dados do Condutor (OrderIndex: 2)
    const section2 = sectionsMap.get(2);
    if (section2) {
      const vehicleData = this.vehicleForm.value;
      allItems.push(
        { sectionId: section2.Id, item: { checklistTemplateItemId: T.driverName, itemName: 'Nome do Condutor', itemType: 'Text', textValue: vehicleData.driverName || '', orderIndex: 1, isCompleted: true } },
        { sectionId: section2.Id, item: { checklistTemplateItemId: T.driverCpf, itemName: 'CPF do Condutor', itemType: 'Text', textValue: vehicleData.driverCpf || '', orderIndex: 2, isCompleted: true } },
        { sectionId: section2.Id, item: { checklistTemplateItemId: T.driverPhone, itemName: 'Telefone do Condutor', itemType: 'Text', textValue: vehicleData.driverPhone || '', orderIndex: 3, isCompleted: true } }
      );
    }

    // Seção 3: Verificações do Veículo (OrderIndex: 3)
    const section3 = sectionsMap.get(3);
    if (section3) {
      const conditionsData = this.conditionsForm.value;
      const fuelLevelMap: { [key: string]: string } = {
        'reserva': 'Reserva',
        '1/4': '1/4',
        '1/2': '1/2',
        '3/4': '3/4',
        'cheio': 'Cheio'
      };
      const tireConditionMap: { [key: string]: string } = {
        'Bom': 'Bom',
        'Medio': 'Médio',
        'Ruim': 'Ruim'
      };
      
      allItems.push(
        { sectionId: section3.Id, item: { checklistTemplateItemId: T.panelKm, itemName: 'KM do Painel', itemType: 'Number', numberValue: conditionsData.panelKm || 0, orderIndex: 1, isCompleted: true } },
        { sectionId: section3.Id, item: { checklistTemplateItemId: T.tireCondition, itemName: 'Condição do Pneu', itemType: 'Select', condition: tireConditionMap[conditionsData.tireCondition] || conditionsData.tireCondition || 'Bom', orderIndex: 2, isCompleted: true } },
        { sectionId: section3.Id, item: { checklistTemplateItemId: T.fuelLevel, itemName: 'Nível de Combustível', itemType: 'Select', condition: fuelLevelMap[conditionsData.fuelLevel] || conditionsData.fuelLevel || '1/2', orderIndex: 3, isCompleted: true } },
        { sectionId: section3.Id, item: { checklistTemplateItemId: T.isDirty, itemName: 'Veículo Está Sujo', itemType: 'Boolean', booleanValue: conditionsData.isDirty || false, orderIndex: 4, isCompleted: true } }
      );
    }

    // Seção 4: Fotos por Lado (OrderIndex: 4)
    const section4 = sectionsMap.get(4);
    if (section4) {
      const photoTemplateIds = CHECKLIST_PHOTO_SIDE_TO_TEMPLATE;
      const photoNames: { [key: string]: string } = {
        'frente': 'Foto Frontal',
        'direita': 'Foto Lado Direito',
        'esquerda': 'Foto Lado Esquerdo',
        'cima': 'Foto Superior',
        'traseira': 'Foto Traseira'
      };
      
      let orderIndex = 1;
      this.sides.forEach(side => {
        if (this.photosRequired[side]) {
          allItems.push({
            sectionId: section4.Id,
            item: {
              checklistTemplateItemId: photoTemplateIds[side],
              itemName: photoNames[side],
              itemType: 'Photo',
              photoUrl: this.photosRequired[side],
              photoSide: photoSideMap[side],
              orderIndex: orderIndex++,
              isCompleted: true
            }
          });
        }
      });
    }

    // Seção 5: Fotos Detalhadas (OrderIndex: 5)
    const section5 = sectionsMap.get(5);
    if (section5) {
      let orderIndex = 1;
      // Criar 4 fotos detalhadas para cada lado (20 fotos no total)
      this.sides.forEach(side => {
        const photoDetailsForSide = this.photoDetails.filter(p => p.side === side);
        photoDetailsForSide.forEach((photoDetail, idx) => {
          if (photoDetail.url) {
            const photoSideLabel = this.mapPhotoSide(photoSideMap[side]);
            allItems.push({
              sectionId: section5.Id,
              item: {
                checklistTemplateItemId: T.detailPhoto,
                itemName: 'Foto ' + photoSideLabel + ' Detalhe ' + (idx + 1),
                itemType: 'Photo',
                photoUrl: photoDetail.url,
                photoSide: photoSideMap[side],
                orderIndex: orderIndex++,
                isCompleted: true
              }
            });
          }
        });
      });
    }

    const section6 = sectionsMap.get(6);
    if (section6) {
      const accessoriesData = this.accessoriesForm.value;
      let orderIndex = 1;
      for (const group of this.accessoryGroups) {
        for (const field of group.items) {
          const value = accessoriesData[field.key];
          if (value === undefined || value === null || value === '') {
            continue;
          }
          allItems.push({
            sectionId: section6.Id,
            item: {
              checklistTemplateItemId: field.templateId,
              itemName: field.label,
              itemType: 'InspectionItem',
              condition: mapSnaToCondition(value),
              orderIndex: orderIndex++,
              isCompleted: true
            }
          });
        }
      }
    }

    // Seção 7: Observações (OrderIndex: 7)
    const section7 = sectionsMap.get(7);
    if (section7) {
      const observationsData = this.observationsForm.value;
      if (observationsData.notes) {
        allItems.push({
          sectionId: section7.Id,
          item: {
            checklistTemplateItemId: T.observations,
            itemName: 'Observações Gerais',
            itemType: 'Text',
            textValue: observationsData.notes,
            orderIndex: 1,
            isCompleted: true
          }
        });
      }
    }

    // Seção 8: Assinatura do Condutor (OrderIndex: 8)
    const section8 = sectionsMap.get(8);
    if (section8) {
      const signatureData = this.signatureForm.value;
      allItems.push(
        { sectionId: section8.Id, item: { checklistTemplateItemId: T.signatureName, itemName: 'Nome do Condutor', itemType: 'Text', textValue: signatureData.name || '', orderIndex: 1, isCompleted: true } },
        { sectionId: section8.Id, item: { checklistTemplateItemId: T.signaturePhone, itemName: 'Telefone do Condutor', itemType: 'Text', textValue: signatureData.phone || '', orderIndex: 2, isCompleted: true } },
        { sectionId: section8.Id, item: { checklistTemplateItemId: T.signatureEmail, itemName: 'Email do Condutor', itemType: 'Text', textValue: signatureData.email || '', orderIndex: 3, isCompleted: true } },
        { sectionId: section8.Id, item: { checklistTemplateItemId: T.signatureCpf, itemName: 'CPF do Condutor', itemType: 'Text', textValue: signatureData.cpf || '', orderIndex: 4, isCompleted: true } }
      );
      
      if (signatureData.signatureDataUrl) {
        allItems.push({
          sectionId: section8.Id,
          item: {
            checklistTemplateItemId: T.signaturePhoto,
            itemName: 'Assinatura do Condutor',
            itemType: 'Photo',
            photoUrl: signatureData.signatureDataUrl,
            photoType: 'Signature',
            orderIndex: 5,
            isCompleted: true
          }
        });
      }
    }

    // Criar todos os itens em sequência
    this.createItemsSequentially(allItems, 0, checklistId);
  }

  /**
   * Cria os itens em sequência para evitar sobrecarga
   */
  private createItemsSequentially(items: Array<{sectionId: number, item: any}>, index: number, checklistId: number): void {
    if (index >= items.length) {
      this.isLoading = false;
      this.errorService.showError(
        'Checklist criado e preenchido com sucesso! Redirecionando para visualização...',
        'info',
        true,
        3000
      );
      setTimeout(() => {
        this.router.navigate(['/checklist', checklistId, 'view']);
      }, 1000);
      return;
    }

    const { sectionId, item } = items[index];
    this.service.createSectionItem(sectionId, item).subscribe({
      next: (response) => {
        // Aguardar um pouco antes de criar o próximo item
        setTimeout(() => {
          this.createItemsSequentially(items, index + 1, checklistId);
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao criar item:', error);
        // Continuar mesmo com erro
        setTimeout(() => {
          this.createItemsSequentially(items, index + 1, checklistId);
        }, 100);
      }
    });
  }

  // ============ Assinatura (canvas em modal) ============
  isSignatureModalOpen = false;
  private sigCanvas?: HTMLCanvasElement;
  private sigCtx?: CanvasRenderingContext2D | null;
  private drawing = false;
  private lastX = 0;
  private lastY = 0;

  openSignatureModal(): void {
    // Criar modal com estilos inline para garantir que funcione
    const modalHTML = `
      <div id="signatureModalBackdrop" style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background: rgba(0,0,0,0.8) !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        z-index: 999999 !important;
      ">
        <div style="
          background: white !important;
          width: 600px !important;
          max-width: 90vw !important;
          border-radius: 10px !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3) !important;
          overflow: hidden !important;
          margin: 20px !important;
        ">
          <div style="
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            padding: 12px 16px !important;
            border-bottom: 1px solid #e5e7eb !important;
            background: white !important;
          ">
            <h3 style="margin: 0 !important; color: #1f2937 !important; font-size: 18px !important;">Assinatura</h3>
            <button id="closeSignatureModal" style="
              background: transparent !important;
              border: none !important;
              font-size: 20px !important;
              cursor: pointer !important;
              padding: 4px 8px !important;
              color: #64748b !important;
            ">×</button>
          </div>
          <div style="padding: 16px !important; background: white !important;">
            <div style="
              width: 100% !important;
              height: 200px !important;
              border: 1px dashed #94a3b8 !important;
              border-radius: 6px !important;
              background: white !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              padding: 8px !important;
            ">
              <canvas id="signatureCanvas" style="
                width: 100% !important;
                height: 100% !important;
                touch-action: none !important;
                border: 1px solid #e5e7eb !important;
                background: white !important;
              "></canvas>
            </div>
            <div style="
              margin-top: 12px !important;
              display: flex !important;
              justify-content: flex-end !important;
              gap: 10px !important;
            ">
              <button id="clearSignature" style="
                background: #e9ecef !important;
                border: none !important;
                padding: 8px 12px !important;
                border-radius: 4px !important;
                cursor: pointer !important;
              ">Limpar</button>
              <button id="saveSignature" style="
                background: #2b3a67 !important;
                color: white !important;
                border: none !important;
                padding: 8px 12px !important;
                border-radius: 4px !important;
                cursor: pointer !important;
              ">Salvar</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    // Adicionar event listeners
    document.getElementById('closeSignatureModal')?.addEventListener('click', () => this.closeSignatureModal());
    document.getElementById('clearSignature')?.addEventListener('click', () => this.clearSignature());
    document.getElementById('saveSignature')?.addEventListener('click', () => this.saveSignatureFromModal());
    document.getElementById('signatureModalBackdrop')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.closeSignatureModal();
    });
    
    // Inicializar canvas
    setTimeout(() => this.initSignatureCanvas(), 100);
  }

  closeSignatureModal(): void {
    const modal = document.getElementById('signatureModalBackdrop');
    if (modal) {
      modal.remove();
    }
    this.isSignatureModalOpen = false;
  }

  private initSignatureCanvas(): void {
    this.sigCanvas = document.getElementById('signatureCanvas') as HTMLCanvasElement | undefined;
    if (!this.sigCanvas) return;
    
    // Ajusta tamanho para container
    const wrapper = this.sigCanvas.parentElement as HTMLElement;
    const width = wrapper.clientWidth || 600;
    const height = Math.max(200, Math.floor(width * 0.4));
    this.sigCanvas.width = width;
    this.sigCanvas.height = height;
    this.sigCtx = this.sigCanvas.getContext('2d');
    if (this.sigCtx) {
      this.sigCtx.lineJoin = 'round';
      this.sigCtx.lineCap = 'round';
      this.sigCtx.lineWidth = 2;
      this.sigCtx.strokeStyle = '#111827';
      this.sigCtx.fillStyle = '#ffffff';
      this.sigCtx.fillRect(0, 0, this.sigCanvas.width, this.sigCanvas.height);
    }
    // Add listeners
    this.attachSignatureListeners();
  }

  private attachSignatureListeners(): void {
    if (!this.sigCanvas) return;
    // Mouse
    this.sigCanvas.addEventListener('mousedown', (e) => this.sigStart(e.offsetX, e.offsetY));
    this.sigCanvas.addEventListener('mousemove', (e) => this.sigMove(e.offsetX, e.offsetY));
    window.addEventListener('mouseup', () => this.sigEnd());
    // Touch
    this.sigCanvas.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault();
      const rect = this.sigCanvas!.getBoundingClientRect();
      const t = e.touches[0];
      this.sigStart(t.clientX - rect.left, t.clientY - rect.top);
    }, { passive: false });
    this.sigCanvas.addEventListener('touchmove', (e: TouchEvent) => {
      e.preventDefault();
      const rect = this.sigCanvas!.getBoundingClientRect();
      const t = e.touches[0];
      this.sigMove(t.clientX - rect.left, t.clientY - rect.top);
    }, { passive: false });
    window.addEventListener('touchend', () => this.sigEnd());
  }

  private sigStart(x: number, y: number): void {
    this.drawing = true;
    this.lastX = x;
    this.lastY = y;
  }

  private sigMove(x: number, y: number): void {
    if (!this.drawing || !this.sigCtx) return;
    this.sigCtx.beginPath();
    this.sigCtx.moveTo(this.lastX, this.lastY);
    this.sigCtx.lineTo(x, y);
    this.sigCtx.stroke();
    this.lastX = x;
    this.lastY = y;
  }

  private sigEnd(): void {
    this.drawing = false;
  }

  clearSignature(): void {
    if (!this.sigCanvas || !this.sigCtx) return;
    this.sigCtx.fillStyle = '#ffffff';
    this.sigCtx.fillRect(0, 0, this.sigCanvas.width, this.sigCanvas.height);
  }

  /**
   * Salva a assinatura do modal (sem gerar PDF)
   */
  saveSignatureFromModal(): void {
    if (!this.sigCanvas) return;
    const dataUrl = this.sigCanvas.toDataURL('image/png');
    this.signatureForm.patchValue({ signatureDataUrl: dataUrl });
    this.closeSignatureModal(); // Fechar o modal após salvar
  }

  /**
   * Gera PDF do checklist após assinatura (chamado pelo botão na página)
   */
  async saveSignature(): Promise<void> {
    // Verificar se há assinatura
    if (!this.signatureForm.value.signatureDataUrl) {
      this.errorService.showError(
        'Por favor, assine o documento primeiro antes de gerar o PDF.',
        'warning',
        true,
        4000
      );
      return;
    }
    
    // Gerar PDF do checklist APÓS assinar o documento
    this.isGeneratingPdf = true;
    try {
      // Buscar dados do veículo para construir o checklist temporário
      const vehicleData = this.vehicleForm.value;
      const plate = vehicleData.plate?.trim();
      let vehicleId = 1;
      let customerId = 1;

      // Se houver placa, buscar o veículo para obter vehicleId e customerId
      if (plate) {
        try {
          const vehicleResponse = await firstValueFrom(this.service.findVehicleByPlate(plate));
          if (vehicleResponse?.success && vehicleResponse?.data && vehicleResponse.data.length > 0) {
            const vehicle = vehicleResponse.data[0];
            vehicleId = vehicle?.Id || vehicle?.id || vehicle?.vehicleId || 1;
            customerId = vehicle?.RentalInfo?.CustomerId || vehicle?.customerId || vehicle?.customer?.id || 1;
          }
        } catch (error) {
        }
      }

      const tempItems = await this.buildTemporaryItemsForPdf();
      const checklistDate = vehicleData.checklistDate 
        ? new Date(`${vehicleData.checklistDate}T${vehicleData.checklistTime || '00:00'}:00`)
        : new Date();
      
      const tempChecklistData = this.buildTemporaryChecklistData(vehicleId, customerId, tempItems, checklistDate);
      const pdfBlob = await this.pdfService.generateChecklistPDF(tempChecklistData);
      // Armazenar o Blob original para upload
      this.generatedPdfBlob = pdfBlob;
      
      // Converter PDF para base64 e armazenar (para exibição)
      const pdfBase64 = await this.pdfService.pdfBlobToBase64(pdfBlob);
      this.generatedPdfBase64 = pdfBase64;
      // Abrir PDF em nova janela para impressão
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          // Aguardar um pouco para o PDF carregar completamente
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        // Se popup foi bloqueado, criar link de download
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `checklist-${new Date().getTime()}.pdf`;
        link.click();
        // Limpar URL após download
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
      }
      
      // Mostrar mensagem de sucesso
      this.errorService.showError(
        'PDF do checklist gerado com sucesso! A janela de impressão foi aberta. O PDF será incluído ao salvar o checklist.',
        'info',
        true,
        5000
      );
    } catch (error) {
      // Não bloquear o salvamento da assinatura se o PDF falhar
      this.errorService.showError(
        'Erro ao gerar o PDF. Tente novamente ou o PDF será gerado ao salvar o checklist.',
        'warning',
        true,
        5000
      );
    } finally {
      this.isGeneratingPdf = false;
    }
  }
  removeDetailPhoto(index: number): void {
    this.photoDetails.splice(index, 1);
  }

  getDamageTypeLabel(damageType: DamageType): string {
    const labels = {
      1: 'Arranhado',
      2: 'Amassado', 
      3: 'Piques',
      4: 'Trincado',
      5: 'Quebrado',
      6: 'Falta'
    };
    return labels[damageType] || 'Desconhecido';
  }

  getPlateFromItems(checklist: ChecklistDetailDto): string | null {
    if (!checklist || !checklist.Sections) {
      return null;
    }
    
    const plate = this.findItemValueByTemplateId(checklist.Sections, T.plate);
    if (!plate) {
      for (const section of checklist.Sections) {
        if (section.Items) {
          const plateItem = section.Items.find((i: any) => 
            i.ChecklistTemplateItemId === T.plate || 
            (i.ItemName && (i.ItemName.toLowerCase().includes('placa') || i.ItemName === 'Placa do Veículo'))
          );
          if (plateItem && plateItem.TextValue) {
            return plateItem.TextValue;
          }
        }
      }
    }
    
    return plate;
  }

  getMileageFromItems(checklist: ChecklistDetailDto): number | null {
    if (!checklist || !checklist.Sections) {
      return null;
    }
    
    return this.findItemNumberValueByTemplateId(checklist.Sections, T.odometer);
  }

  getPanelKmFromItems(checklist: ChecklistDetailDto): number | null {
    if (!checklist || !checklist.Sections) return null;
    return this.findItemNumberValueByTemplateId(checklist.Sections, T.panelKm);
  }

  /**
   * Navega de volta - se veio de um contrato, volta para o contrato, senão volta para a lista de checklists
   */
  navigateBack(): void {
    if (this.contractIdFrom) {
      this.router.navigate(['/contracts', this.contractIdFrom, 'view']);
    } else {
      this.router.navigate(['/checklist']);
    }
  }

  ngOnDestroy(): void {
    // Limpar recursos da câmera quando o componente for destruído
    this.closeCameraModal();
  }
}

